// Frontend: CoachDashboard.tsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { flushSync } from 'react-dom';
import useAdminStore from '../store/adminStore';
import useAppStore from '../store/index'; 
import { auth, db } from '../config/firebase'; 
// 💉 CHIRURGIE-FIX: query & where reaktiviert für serverseitig gefilterten Invoices-Listener
import { doc, getDoc, setDoc, collection, onSnapshot, deleteDoc, query, where, addDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import CoachInvoice from '../components/CoachInvoice'; 
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import SentinelRadar from '../components/SentinelRadar';
import StripeRadar from '../components/StripeRadar';
import ChangeRadarBadge from '../components/changeRadar/ChangeRadarBadge';
import AktivCounterRadar from '../components/aktivcounter/AktivCounterRadar';
import {
    resolveMemberById,
    resolveClientCoach,
    clientMatchesMember,
    isCoachLikeMember,
    mergeTeamPool,
    getAssignableTeam,
} from '../utils/teamResolve';
import { calculateClientMonthlyFee } from '../utils/clientPricing';
import { resolvePremiumToolOwnerBilling } from '../utils/premiumCostBridge';
import { formatEuroDe, getCountryMwstSatz, netToGross } from '../utils/billingGross';
import { resolveStripeBillingBadge, STRIPE_PAYMENT_LOCK_ARMED } from '../utils/stripeBilling';
import { formatMandantNrDisplay, isVorabOrLegacyMandantNr, normalizeVorabMandantNr, resolveCoachInitialLetter, resolveTarifBadgeDisplay, resolveVertragsbeginnDisplay } from '../utils/mandantNrFormat';
import { Menu as MenuLucideIcon, X as XLucideIcon } from 'lucide-react';
import { MandantNrPill } from '../components/MandantNrPill';
import ProductivityDock from '../components/ProductivityDock';
import MobileTrackerView from '../components/socialMedia/MobileTrackerView';
import useSocialMediaStore from '../store/socialMediaStore';
import {
    SOCIAL_MEDIA_MONTHLY_TARGET_POINTS,
    achievementPercent,
    billingMalusEuro,
    billingMalusRate,
    clampVacationDays,
    displayMalusRate,
    isBillingMalusExemptEmail,
    previousMonthKey,
    targetPointsFromVacation,
} from '../utils/socialMediaScoring';

// --- AUSGELAGERTE KOMPONENTEN ---
// WICHTIG: Die Pfade gehen jetzt korrekt in den Ordner "components"
import FinanzTresor from '../components/FinanzTresor';
import RechnungsCenter from '../components/RechnungsCenter';
import CoachEInvoiceSettingsModal from '../components/coach/CoachEInvoiceSettingsModal';
import CoachSeminarOverview from '../components/coach/CoachSeminarOverview';
import {
  EMPTY_COACH_EINVOICE_PROFILE,
  PREISEXPERT_OWNER_EINVOICE_DEFAULTS,
  type CoachEInvoiceProfile,
} from '../types/coachEInvoiceProfile';
import { loadCoachEInvoiceProfile } from '../services/coachEInvoiceFirebaseService';
import {
  applyCoachEInvoiceToMemberFields,
  assessCoachEInvoiceProfile,
  buildCoachHonorariumZugferdPayload,
  buildClientBuyerCustomer,
  buildCoachClientZugferdPayload,
  coachMemberToEInvoiceProfile,
} from '../utils/coach/coachEInvoiceProfileUtils';
import {
  attachZugferdToPdfBytes,
  triggerPdfBytesDownload,
} from '../utils/coach/coachZugferdPdfAttach';
import { buildCoachHonorariumPdfDoc } from '../utils/coach/coachHonorariumPdfGenerator';
import CoachInvoiceModal from '../components/CoachInvoiceModal'; // 💉 ROUTING-FIX: Korrektes Formular geladen
import CoachingTerminePanel from '../components/admin/CoachingTerminePanel';
import { IntakeHubModal } from './CoachIntakeAdminPage';
import {
  IntakeCountPill,
  IntakePendingAdoptionPill,
  useIntakeMenuCounts,
} from '../components/intake/IntakeMenuBadge';
import useEventStore, { shouldShowMissingTopicReminder } from '../store/eventStore';

// --- ICONS ---
const IconBase = ({ children, size = 20, className = "" }: any) => <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>{children}</svg>;
const LogOutIcon = (props: any) => <IconBase {...props}><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></IconBase>;
const UsersIcon = (props: any) => <IconBase {...props}><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><polyline points="17 11 19 13 23 9"/></IconBase>;
const BriefcaseIcon = (props: any) => <IconBase {...props}><rect width="20" height="14" x="2" y="7" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></IconBase>;
const ExternalLinkIcon = (props: any) => <IconBase {...props}><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></IconBase>;
const CheckCircleIcon = (props: any) => <IconBase {...props}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><path d="m9 11 3 3L22 4"/></IconBase>;
const FilterIcon = (props: any) => <IconBase {...props}><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></IconBase>;
const ShieldAlertIcon = (props: any) => <IconBase {...props}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></IconBase>;
const DownloadIcon = (props: any) => <IconBase {...props}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></IconBase>;
const XIcon = (props: any) => <IconBase {...props}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></IconBase>;
const FileTextIcon = (props: any) => <IconBase {...props}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></IconBase>;
const PlusIcon = (props: any) => <IconBase {...props}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></IconBase>;
const SearchIcon = (props: any) => <IconBase {...props}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></IconBase>;
const MailIcon = (props: any) => <IconBase {...props}><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></IconBase>;
const ClockIcon = (props: any) => <IconBase {...props}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></IconBase>;
const AlertTriangleIcon = (props: any) => <IconBase {...props}><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></IconBase>;
const CalendarIcon = (props: any) => <IconBase {...props}><rect width="18" height="18" x="3" y="4" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></IconBase>;
const HeartIcon = (props: any) => <IconBase {...props}><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></IconBase>;
const ActivityIcon = (props: any) => <IconBase {...props}><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></IconBase>;

const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);
};

const isOwnerProfile = (profileName: any, profileEmail?: any) => {
    const nameLower = String(profileName || '').toLowerCase();
    const emailLower = String(profileEmail || '').toLowerCase();
    return (
        nameLower.includes('christian') || 
        nameLower === 'admin' || 
        nameLower === 'preisexpert' ||
        emailLower.includes('erfolg@friseur-coaching.de')
    );
};

const calculateMonthlyFee = (client: any, globalSettings: any) => {
    if (!client) return 0;

    const billingStatus = client.billingStatus || client.clientData?.billingStatus;
    const customerNumber = String(
        client.customerNumber || client.clientData?.customerNumber || ''
    ).trim();
    const isExistingCustomer = client.isExistingCustomer ?? client.clientData?.isExistingCustomer;
    const isBestandskunde = client.isBestandskunde ?? client.clientData?.isBestandskunde;
    const isLegacy = Boolean(client.isLegacy || client.clientData?.isLegacy);
    const fixedFee = client.monthlyFee ?? client.clientData?.monthlyFee;

    // 1. GATING FÜR MANDANTEN IN PRÜFUNG
    // pending_approval ODER noch keine echte Kd.-Nr. ODER expliziter Neukunde ohne active
    // ODER korrupte Platzhalter-Nr. (Sync hat Registrierung überschrieben)
    const mandantNrRaw = String(client.mandantNr || client.clientData?.mandantNr || '').trim();
    const isCorruptPlaceholderNr =
        !mandantNrRaw ||
        mandantNrRaw === 'WIEDERHERGESTELLT' ||
        mandantNrRaw === '---' ||
        /^warte/i.test(mandantNrRaw);

    const isPendingApproval =
        billingStatus === 'pending_approval' ||
        customerNumber === 'PENDING' ||
        isCorruptPlaceholderNr ||
        (billingStatus !== 'active' &&
            !isLegacy &&
            (isExistingCustomer === false || isBestandskunde === false));

    if (isPendingApproval) {
        // Vorabkunde: Fester manueller Vorab-Betrag bleibt erhalten
        if (isLegacy && typeof fixedFee === 'number' && fixedFee > 0) {
            return fixedFee;
        }
        // Nicht freigegebener Neukunde: 0 € (kein Honorar / Umsatz)
        return 0;
    }

    // 2. VORABKUNDEN / FIXPREISE (BESTAND)
    // Vorabkunden: immer gespeicherten Fixpreis, nie Auto-Tarif
    if (isLegacy) {
        if (typeof fixedFee === 'number' && fixedFee >= 0) return fixedFee;
        if (typeof fixedFee === 'string') {
            const cleaned = fixedFee.trim().replace(/\./g, '').replace(',', '.');
            const parsed = parseFloat(cleaned);
            if (!isNaN(parsed) && parsed >= 0) return parsed;
        }
        return 0;
    }

    // 3. REGULÄRE PRICING-ENGINE (AKTIVE MANDANTEN & BESTAND)
    return calculateClientMonthlyFee({
        settings: { global: globalSettings },
        client,
        isProBono: client.proBonoActive || client.clientData?.proBonoActive === true,
    });
};

const extractVatInfo = (client: any) => {
    const data = client?.clientData || {};
    const type = data.steuerStatus || data.vatType || client?.vatType || 'Standard';
    const typeLower = String(type).toLowerCase();
    let rate = 19; 
    if (typeLower.includes('reverse') || typeLower.includes('podo') || typeLower.includes('frei') || typeLower.includes('0')) rate = 0;
    return { vatType: type, vatRate: rate };
};

const getAmount = (str: string | number) => {
    if (!str) return 0;
    if (typeof str === 'number') return str;
    const cleaned = String(str).replace(/\./g, '').replace(',', '.');
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : parsed;
};

const getCurrentMonthTag = () => {
    const d = new Date();
    return `${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

// -----------------------------------------------------------------------------
// PANZERGLAS V2 DRUCKVORLAGE FÜR MANDANTEN-RECHNUNGEN
// -----------------------------------------------------------------------------
const ClientManualInvoicePrintTemplate: React.FC<{ data: any }> = ({ data }) => {
    if (!data) return null;

    // Snapshot-Felder (Fremdkunde / neue Belege) primär; CRM-Join nur als Altdaten-Fallback
    const billFirma = String(
        data.clientFirma || data.clientCompany || data.client?.firmenname || ''
    ).trim();
    const billName = String(
        data.clientName || data.client?.fullName || ''
    ).trim();
    const billStrasse = String(
        data.clientStrasse || data.client?.clientData?.strasse || ''
    ).trim();
    const billPlz = String(
        data.clientPlz || data.client?.clientData?.plz || ''
    ).trim();
    const billOrt = String(
        data.clientOrt || data.client?.clientData?.ort || ''
    ).trim();
    const billLand = String(
        data.clientLand || data.client?.clientData?.land || data.client?.land || 'Deutschland'
    ).trim() || 'Deutschland';

    const landRaw = billLand.toUpperCase();
    const isDE = landRaw === 'DE' || landRaw === 'DEUTSCHLAND' || landRaw === 'D';
    // Explizit bei Rechnungsstellung; Fallback für ältere Rechnungen ohne Flag: Land
    const reverseCharge =
        data.reverseCharge === true ||
        (typeof data.reverseCharge !== 'boolean' && Number(data.vatRate) === 0) ||
        (typeof data.reverseCharge !== 'boolean' && data.vatRate == null && !isDE);
    const vatRate = reverseCharge ? 0 : 0.19;
    const netto = parseFloat(data.netAmount) || 0;
    const vat = netto * vatRate;
    const brutto = netto + vat;

    const resolveCreatedDate = (): Date => {
        const raw = data.createdAt;
        if (raw?.toDate) return raw.toDate();
        if (typeof raw === 'string') {
            const d = new Date(raw);
            if (!Number.isNaN(d.getTime())) return d;
        }
        if (typeof raw?.seconds === 'number') return new Date(raw.seconds * 1000);
        if (data.serviceDate) {
            const d = new Date(data.serviceDate);
            if (!Number.isNaN(d.getTime())) return d;
        }
        return new Date();
    };
    const dateObj = resolveCreatedDate();
    const issueDate = dateObj.toLocaleDateString('de-DE');
    const dueCalc = new Date(dateObj.getTime());
    dueCalc.setDate(dueCalc.getDate() + 10);
    const dueDate = dueCalc.toLocaleDateString('de-DE');

    const coachFirmName = data.coach?.invoiceCompanyName;
    const coachAccountHolder = data.coach?.invoiceAccountHolder || data.coach?.name || 'Coach';
    const coachStreet = data.coach?.invoiceStreet || '---';
    const coachCity = data.coach?.invoiceCity || '---';
    const coachEmail = data.coach?.email || '';

    const invoiceNumber = data.invoiceNumber || '---';

    return (
        <div className="invoice-print-container" style={{ width: '794px', minHeight: '1123px', padding: '60px', boxSizing: 'border-box', backgroundColor: '#ffffff', color: '#000000', fontFamily: 'Arial, sans-serif' }}>
            {data.isCorrection === true ? (
                <div style={{ marginBottom: '16px', padding: '10px 14px', backgroundColor: '#fffbeb', border: '1px solid #f59e0b', color: '#92400e', fontSize: '12px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Korrektur / Nachbearbeitung — ersetzt bzw. berichtigt die zuvor versendete Fassung
                </div>
            ) : null}
            <table style={{ width: '674px', tableLayout: 'fixed', borderCollapse: 'collapse', marginBottom: '30px' }}>
                <tbody>
                    <tr>
                        <td style={{ width: '337px', verticalAlign: 'top', padding: '0', borderBottom: '2px solid #0f172a', paddingBottom: '20px' }}>
                            {data.coach?.invoiceLogoUrl ? (
                                <img src={`/assets/rechnung/${data.coach.invoiceLogoUrl}`} alt="Logo" style={{ maxHeight: '60px', maxWidth: '200px', objectFit: 'contain', marginBottom: '10px' }} />
                            ) : (
                                <h1 style={{ fontSize: '32px', margin: '0 0 5px 0', fontWeight: 'bold', color: '#0f172a' }}>RECHNUNG</h1>
                            )}
                            <p style={{ color: '#d97706', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase', margin: '0 0 20px 0' }}>Consulting / Coaching</p>
                            <div style={{ fontSize: '13px', lineHeight: '1.4' }}>
                                <p style={{ color: '#94a3b8', fontSize: '10px', textTransform: 'uppercase', margin: '0 0 5px 0' }}>Rechnung an:</p>
                                {billFirma ? <><strong style={{ fontWeight: 'bold' }}>{billFirma}</strong><br /></> : null}
                                {billName || '—'}
                                {billStrasse ? <><br />{billStrasse}</> : null}
                                {(billPlz || billOrt) ? <><br />{[billPlz, billOrt].filter(Boolean).join(' ')}</> : null}
                                {(billStrasse || billPlz || billOrt) && billLand ? <><br />{billLand}</> : null}
                            </div>
                        </td>
                        <td style={{ width: '337px', verticalAlign: 'top', textAlign: 'right', padding: '0', borderBottom: '2px solid #0f172a', paddingBottom: '20px' }}>
                            <div style={{ backgroundColor: '#f8fafc', padding: '15px', border: '1px solid #e2e8f0', textAlign: 'right', fontSize: '12px', lineHeight: '1.4', color: '#475569', marginLeft: 'auto', width: '220px' }}>
                                {coachFirmName ? (
                                    <>
                                        <p style={{ margin: '0 0 2px 0', fontSize: '11px' }}>Kontoinhaber: {coachAccountHolder}</p>
                                        <p style={{ fontWeight: 'bold', fontSize: '14px', color: '#000000', margin: '0 0 8px 0' }}>Firma {coachFirmName}</p>
                                    </>
                                ) : (
                                    <p style={{ fontWeight: 'bold', fontSize: '14px', color: '#000000', margin: '0 0 8px 0' }}>{coachAccountHolder}</p>
                                )}
                                <p style={{ margin: '0 0 2px 0' }}>{coachStreet}</p>
                                <p style={{ margin: '0' }}>{coachCity}</p>
                                <p style={{ color: '#d97706', fontWeight: 'bold', margin: '10px 0 0 0' }}>{coachEmail}</p>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>

            <table style={{ width: '674px', tableLayout: 'fixed', borderCollapse: 'collapse', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', marginBottom: '30px' }}>
                <tbody>
                    <tr>
                        <td style={{ width: '224px', padding: '15px' }}>
                            <span style={{ display: 'block', color: '#94a3b8', fontSize: '10px', textTransform: 'uppercase', marginBottom: '4px' }}>Rechnungs-Nr.</span>
                            <span style={{ fontSize: '14px', fontWeight: 'bold', fontFamily: 'monospace', color: '#000000' }}>{invoiceNumber}</span>
                        </td>
                        <td style={{ width: '225px', padding: '15px', borderLeft: '1px solid #e2e8f0' }}>
                            <span style={{ display: 'block', color: '#94a3b8', fontSize: '10px', textTransform: 'uppercase', marginBottom: '4px' }}>Datum</span>
                            <span style={{ fontSize: '14px', fontWeight: 'bold' }}>{issueDate}</span>
                        </td>
                        <td style={{ width: '225px', padding: '15px', textAlign: 'right', borderLeft: '1px solid #e2e8f0' }}>
                            <span style={{ display: 'block', color: '#94a3b8', fontSize: '10px', textTransform: 'uppercase', marginBottom: '4px' }}>Leistungsdatum</span>
                            <span style={{ fontSize: '14px', fontWeight: 'bold' }}>{data.serviceDate}</span>
                        </td>
                    </tr>
                </tbody>
            </table>

            <table style={{ width: '674px', tableLayout: 'fixed', borderCollapse: 'collapse', marginBottom: '20px' }}>
                <thead>
                    <tr>
                        <th style={{ width: '474px', padding: '10px 5px', borderBottom: '2px solid #1e293b', textAlign: 'left', fontSize: '11px', textTransform: 'uppercase', color: '#0f172a' }}>Beschreibung / Leistung</th>
                        <th style={{ width: '60px', padding: '10px 5px', borderBottom: '2px solid #1e293b', textAlign: 'center', fontSize: '11px', textTransform: 'uppercase', color: '#0f172a' }}>MwSt.</th>
                        <th style={{ width: '140px', padding: '10px 5px', borderBottom: '2px solid #1e293b', textAlign: 'right', fontSize: '11px', textTransform: 'uppercase', color: '#0f172a' }}>Netto</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td style={{ padding: '15px 5px', borderBottom: '1px solid #e2e8f0', verticalAlign: 'top' }}>
                            <strong style={{ display: 'block', fontSize: '14px', marginBottom: '4px', color: '#020617' }}>{data.invoiceTitle}</strong>
                        </td>
                        <td style={{ padding: '15px 5px', borderBottom: '1px solid #e2e8f0', verticalAlign: 'top', textAlign: 'center', color: '#64748b', fontSize: '13px' }}>{reverseCharge ? '0%' : '19%'}</td>
                        <td style={{ padding: '15px 5px', borderBottom: '1px solid #e2e8f0', verticalAlign: 'top', textAlign: 'right', fontSize: '14px', fontWeight: 'bold' }}>{formatCurrency(netto)}</td>
                    </tr>
                </tbody>
            </table>

            <table style={{ width: '674px', tableLayout: 'fixed', borderCollapse: 'collapse', marginBottom: '30px' }}>
                <tbody>
                    <tr>
                        <td style={{ width: '400px' }}></td>
                        <td style={{ width: '274px' }}>
                            <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: '13px' }}>
                                <tbody>
                                    <tr>
                                        <td style={{ width: '134px', padding: '5px 0', color: '#64748b' }}>Summe (Netto):</td>
                                        <td style={{ width: '140px', padding: '5px 0', textAlign: 'right' }}>{formatCurrency(netto)}</td>
                                    </tr>
                                    <tr>
                                        <td style={{ padding: '5px 0', color: '#64748b', borderBottom: '1px solid #e2e8f0', paddingBottom: '10px' }}>MwSt. {reverseCharge ? '(Ausland / RC)' : '(19%)'}:</td>
                                        <td style={{ padding: '5px 0', textAlign: 'right', borderBottom: '1px solid #e2e8f0', paddingBottom: '10px' }}>{formatCurrency(vat)}</td>
                                    </tr>
                                    <tr>
                                        <td style={{ padding: '15px 0', fontWeight: 'bold', fontSize: '14px' }}>Gesamtbetrag:</td>
                                        <td style={{ padding: '15px 0', textAlign: 'right', color: '#d97706', fontSize: '18px', fontWeight: 'bold' }}>{formatCurrency(brutto)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </td>
                    </tr>
                </tbody>
            </table>

            <div style={{ width: '674px', backgroundColor: '#f8fafc', padding: '20px', border: '1px solid #e2e8f0', boxSizing: 'border-box', fontSize: '12px', lineHeight: '1.5' }}>
                <p style={{ margin: '0 0 15px 0' }}>Bitte überweisen Sie den Gesamtbetrag bis zum <strong>{dueDate}</strong> auf folgendes Konto:</p>
                <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', backgroundColor: '#ffffff', border: '1px solid #f1f5f9' }}>
                    <tbody>
                        <tr>
                            <td style={{ width: '100%', padding: '10px', color: '#334155' }}>
                                Kontoinhaber: <strong style={{color: '#000', marginRight: '20px'}}>{coachAccountHolder}</strong>
                                Steuer-Nr.: <strong style={{color: '#000'}}>{data.coach?.invoiceTaxId || '---'}</strong>
                            </td>
                        </tr>
                        <tr>
                            <td style={{ width: '100%', padding: '10px', color: '#334155', borderTop: '1px solid #f1f5f9' }}>
                                IBAN: <strong style={{color: '#000', marginRight: '20px'}}>{data.coach?.invoiceIban || '---'}</strong>
                                BIC: <strong style={{color: '#000'}}>{data.coach?.invoiceBic || '---'}</strong>
                            </td>
                        </tr>
                    </tbody>
                </table>
                {reverseCharge && (
                    <p style={{ margin: '15px 0 0 0', color: '#94a3b8', fontStyle: 'italic', fontSize: '11px' }}>
                        Steuerschuldnerschaft des Leistungsempfängers (Reverse-Charge-Verfahren / Ausland) —
                        keine deutsche MwSt. ausgewiesen.
                    </p>
                )}
            </div>
        </div>
    );
};

// -----------------------------------------------------------------------------
// HAUPT-DASHBOARD
// -----------------------------------------------------------------------------

const CoachDashboard: React.FC = () => {
    const { crmClients, pendingApprovals, isLoadingPending, fetchClients, startImpersonation } = useAdminStore();
    
    const { settings, team, teamRegistry } = useAppStore(state => ({
        settings: state.settings,
        team: state.team || [],
        teamRegistry: state.teamRegistry || [],
    }));
    
    const fullTeam = useMemo(() => mergeTeamPool(team, teamRegistry), [team, teamRegistry]);

    // Finanzen: Coach + Support (Omar). Zuordnung bleibt über coachDropdownTeam ohne Support.
    const activeTeam = useMemo(
        () => fullTeam.filter((m: any) => m && isCoachLikeMember(m) && !isOwnerProfile(m.name, m.email)),
        [fullTeam]
    );
    // Ansicht-Dropdown: Coaches UND Admins (Martina) — nur Support (Omar) und
    // der Inhaber selbst bleiben draußen
    const coachDropdownTeam = useMemo(
        () => fullTeam.filter((m: any) => {
            if (!m || isOwnerProfile(m.name, m.email)) return false;
            const category = String(m.category || '').toLowerCase();
            const role = String(m.role || '').toLowerCase();
            if (category === 'support' || role === 'support') return false;
            return ['coach', 'admin', 'superadmin'].includes(category)
                || ['coach', 'admin', 'superadmin'].includes(role);
        }),
        [fullTeam]
    );
    
    const globalSettings = useMemo(() => (settings as any)?.global || {}, [settings]);
    const TEAM_SPLIT = useMemo(() => (settings as any)?.splits || {}, [settings]);

    /** Direkt-Routing: /pepulse | /coach/pepulse */
    const pepulsePathFlags = useMemo(() => {
        const raw = String(window.location.pathname || '').toLowerCase();
        const path = raw.replace(/\/+$/, '') || '/';
        const wantsPepulseTab = path.includes('pepulse');
        const isPepulseStandalone = path === '/pepulse';
        return { wantsPepulseTab, isPepulseStandalone };
    }, []);

    const [activeTab, setActiveTab] = useState<'crm' | 'finanzen' | 'rechnungen' | 'pepulse'>(() =>
        pepulsePathFlags.wantsPepulseTab ? 'pepulse' : 'crm'
    );
    const isPepulseStandalone = pepulsePathFlags.isPepulseStandalone;
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [showIntakeHub, setShowIntakeHub] = useState(false);
    const { openCount: openIntakeCount, pendingAdoptionCount } = useIntakeMenuCounts();
    const [termineClient, setTermineClient] = useState<{
        id: string;
        name: string;
        coachId?: string;
        coachName?: string;
        coachAvatarUrl?: string;
    } | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [showOnlyMine, setShowOnlyMine] = useState<boolean>(false);
    const [adminFilter, setAdminFilter] = useState<string>('Alle');
    const [radarFilter, setRadarFilter] = useState<'all' | 'online' | 'inactive60' | 'acute90'>('all');
    
    const [isInvoiceExpanded, setIsInvoiceExpanded] = useState<boolean>(false);
    const [previewInvoiceData, setPreviewInvoiceData] = useState<any>(null);
    const [sections, setSections] = useState({ revenue: false, clearing: false, history: false, archives: true });

    /** PEPulse M-1: Punkte + Urlaub des Vormonats je Coach-E-Mail */
    const pepulsePrevMonth = useMemo(() => previousMonthKey(), []);
    const [pepulseM1PointsByEmail, setPepulseM1PointsByEmail] = useState<Record<string, number>>({});
    const [pepulseM1VacationByEmail, setPepulseM1VacationByEmail] = useState<Record<string, number>>({});
    const isDeductionActive = useSocialMediaStore((s) => s.isDeductionActive);

    useEffect(() => {
        useSocialMediaStore.getState().ensureSettingsLoaded();
        return () => useSocialMediaStore.getState().unsubscribeSettings();
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const store = useSocialMediaStore.getState();
                const logs = await store.fetchMonthAllLogs(pepulsePrevMonth);
                const meta = await store.fetchMonthMeta(pepulsePrevMonth);
                if (cancelled) return;

                const pointsMap: Record<string, number> = {};
                for (const log of logs) {
                    const email = String(log.userEmail || '').trim().toLowerCase();
                    if (!email) continue;
                    pointsMap[email] = (pointsMap[email] || 0) + (Number(log.points) || 0);
                }
                setPepulseM1PointsByEmail(pointsMap);

                const uidToEmail = new Map<string, string>();
                fullTeam.forEach((m: any) => {
                    const uid = String(m?.id || '').trim();
                    const em = String(m?.email || '').trim().toLowerCase();
                    if (uid && em) uidToEmail.set(uid, em);
                });

                const vacMap: Record<string, number> = {};
                Object.values(meta).forEach((m) => {
                    let email = String(m.userEmail || '').trim().toLowerCase();
                    if (!email && m.userId) email = uidToEmail.get(String(m.userId)) || '';
                    if (!email) return;
                    vacMap[email] = clampVacationDays(m.vacationDays);
                });
                setPepulseM1VacationByEmail(vacMap);
            } catch (e) {
                console.error('[PEPulse] M-1 Daten laden fehlgeschlagen:', e);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [pepulsePrevMonth, fullTeam]);

    // 💉 ARCHIV ZIEHHARMONIKA STATE
    const [expandedArchiveMonths, setExpandedArchiveMonths] = useState<Record<string, boolean>>({});

    const [clearingItems, setClearingItems] = useState<any[]>([]);
    const [historyItems, setHistoryItems] = useState<any[]>([]);
    const [isClearingLoading, setIsClearingLoading] = useState(true);
    const [isDirty, setIsDirty] = useState(false);
    
    const [archiveItems, setArchiveItems] = useState<any[]>([]);
    const [isArchiveLoading, setIsArchiveLoading] = useState(true);

    const [, setDeleteModalOpen] = useState<{ isOpen: boolean, id: string | null }>({ isOpen: false, id: null });

    const [isGeneratingInvoices, setIsGeneratingInvoices] = useState(false);
    const [invoiceSuccessMsg, setInvoiceSuccessMsg] = useState('');

    const [clientInvoices, setClientInvoices] = useState<any[]>([]);
    const [isClientInvoicesLoading, setIsClientInvoicesLoading] = useState(true);
    const [invoiceView, setInvoiceView] = useState<'list' | 'create'>('list');
    const [isCreatingClientInvoice, setIsCreatingClientInvoice] = useState(false);
    const [selectedHistoryInvoice, setSelectedHistoryInvoice] = useState<any>(null);
    const [editingClientInvoice, setEditingClientInvoice] = useState<any | null>(null); 
    const [isSendingClientInvoiceMail, setIsSendingClientInvoiceMail] = useState(false);

    const [coachEInvoiceProfile, setCoachEInvoiceProfile] = useState<CoachEInvoiceProfile>({
        ...EMPTY_COACH_EINVOICE_PROFILE,
    });
    const [eInvoiceSettingsOpen, setEInvoiceSettingsOpen] = useState(false);

    // Auth reaktiv verfolgen: auth.currentUser kann beim ersten Render noch null
    // sein — ohne Subscription verschwinden sonst Admin-Controls (Ansicht-Dropdown)
    const [authUser, setAuthUser] = useState(() => auth.currentUser);
    useEffect(() => auth.onAuthStateChanged((user) => setAuthUser(user)), []);

    const currentUserId = authUser?.uid || '';
    const currentUserEmail = authUser?.email?.toLowerCase() || '';
    
    const currentUserProfile = fullTeam.find(m => 
        (m && m.id === currentUserId) || 
        (m && m.email && m.email.toLowerCase() === currentUserEmail)
    );
    
    const currentUserName = currentUserProfile?.name || 'Christian'; 
    
    const isAdminUser = 
        currentUserEmail === 'erfolg@friseur-coaching.de' || 
        currentUserEmail.includes('christian') || 
        currentUserEmail.includes('martina') || 
        currentUserProfile?.role === 'admin' || 
        currentUserProfile?.role === 'superadmin';

    const isSupportUser =
        String(currentUserProfile?.category || '').toLowerCase() === 'support' ||
        String(currentUserProfile?.role || '').toLowerCase() === 'support';

    // Support (Omar) ausgenommen. Owner inkl. — stellt selbst Mandanten-Rechnungen aus.
    const canManageEInvoice = !isSupportUser;
    const isCurrentUserOwner = isOwnerProfile(currentUserName, currentUserEmail);
    const eInvoiceStatus = useMemo(
        () => assessCoachEInvoiceProfile(coachEInvoiceProfile),
        [coachEInvoiceProfile]
    );

    useEffect(() => {
        if (!canManageEInvoice || !currentUserId) return;
        let cancelled = false;
        void (async () => {
            const loaded = await loadCoachEInvoiceProfile(currentUserId);
            if (!cancelled) setCoachEInvoiceProfile(loaded);
        })();
        return () => {
            cancelled = true;
        };
    }, [canManageEInvoice, currentUserId]);

    const canAccessPepulse = !isSupportUser && (
        isAdminUser ||
        String(currentUserProfile?.category || '').toLowerCase() === 'coach' ||
        String(currentUserProfile?.role || '').toLowerCase() === 'coach' ||
        currentUserEmail === 'erfolg@friseur-coaching.de' ||
        currentUserEmail === 'coaching@preisexpert.info'
    );

    // Themen-Reminder: Coaches sehen Banner wenn Monats-Thema in ≤10 Tagen fehlt
    const isCoachUser =
        String(currentUserProfile?.category || '').toLowerCase() === 'coach' ||
        String(currentUserProfile?.role || '').toLowerCase() === 'coach';
    const isWebdesignUser =
        String(currentUserProfile?.category || '').toLowerCase() === 'webdesign' ||
        String(currentUserProfile?.role || '').toLowerCase() === 'webdesign';
    const eventList = useEventStore((s) => s.events);
    const fetchEvents = useEventStore((s) => s.fetchEvents);
    useEffect(() => {
        if (!isCoachUser || isSupportUser || isWebdesignUser || isPepulseStandalone) return;
        void fetchEvents();
    }, [isCoachUser, isSupportUser, isWebdesignUser, isPepulseStandalone, fetchEvents]);
    const showMissingTopicReminder =
        isCoachUser &&
        !isSupportUser &&
        !isWebdesignUser &&
        !isPepulseStandalone &&
        eventList.some((ev) => shouldShowMissingTopicReminder(ev));

    useEffect(() => {
        if (isPepulseStandalone) {
            setActiveTab('pepulse');
            setIsMobileMenuOpen(false);
        }
    }, [isPepulseStandalone]);

    useEffect(() => {
        if (activeTab === 'pepulse' && !canAccessPepulse && !isPepulseStandalone) {
            setActiveTab('crm');
        }
    }, [activeTab, canAccessPepulse, isPepulseStandalone]);

    const isGlobalView = isAdminUser && adminFilter === 'Alle';
    const filteredCoachMember = adminFilter !== 'Alle' ? resolveMemberById(adminFilter, fullTeam) : null;
    const targetCoachName = isGlobalView
        ? 'Christian'
        : filteredCoachMember?.name || currentUserName;

    // Profil für den "Nur Meine"-Mandantenfilter: immer das ECHTE Team-Profil
    // (nicht das Rechnungsprofil mit Auth-UID), sonst matcht kein Betreuer-Eintrag
    const filterMemberProfile = useMemo(() => {
        if (adminFilter !== 'Alle' && filteredCoachMember) return filteredCoachMember;
        if (currentUserProfile) return currentUserProfile;
        if (!currentUserId) return null;
        return { id: currentUserId, name: currentUserName, email: currentUserEmail };
    }, [adminFilter, filteredCoachMember, currentUserProfile, currentUserId, currentUserName, currentUserEmail]);

    const previewCoachProfile = useMemo(() => {
        const ownerInvoiceProfile = {
            id: currentUserId || 'admin_christian',
            name: 'Christian Funk',
            email: 'erfolg@friseur-coaching.de',
            invoiceCompanyName: 'PreisExpert',
            invoiceStreet: 'Rotehahnstraße 2',
            invoiceCity: '21335 Lüneburg',
            invoiceIban: 'DE43 2406 0300 8523 1266 00',
            invoiceBic: 'GENODEF1NBU',
            invoiceTaxId: 'DE235671457',
            invoiceAccountHolder: 'Christian Funk'
        };

        if (adminFilter === 'Alle') {
            return isAdminUser ? ownerInvoiceProfile : (currentUserProfile || ownerInvoiceProfile);
        }

        const selectedMember = resolveMemberById(adminFilter, fullTeam);
        if (selectedMember && isOwnerProfile(selectedMember.name, selectedMember.email)) {
            return ownerInvoiceProfile;
        }
        return selectedMember || currentUserProfile;
    }, [fullTeam, adminFilter, currentUserProfile, currentUserId, isAdminUser]);

    const previewCoachProfileWithEInvoice = useMemo(() => {
        return applyCoachEInvoiceToMemberFields(
            previewCoachProfile as Record<string, unknown>,
            coachEInvoiceProfile
        );
    }, [previewCoachProfile, coachEInvoiceProfile]);

    useEffect(() => {
        fetchClients();
    }, [fetchClients, activeTab]);

    useEffect(() => {
        const loadSystemData = async () => {
            try {
                const clearingRef = doc(db, 'system_data', 'clearing');
                const clearingSnap = await getDoc(clearingRef);
                if (clearingSnap.exists()) {
                    const data = clearingSnap.data();
                    let loadedClearing = data.clearingItems || [];
                    let loadedHistory = data.historyItems || [];
                    let needsSave = false;
                    const currentTag = getCurrentMonthTag();
                    const activeItems = loadedClearing.filter((item: any) => {
                        if (item && item.interval === 'einmalig' && item.targetMonth && item.targetMonth !== currentTag) {
                            loadedHistory = [{ ...item, desc: `${item.desc || ''} (Auto-Archiviert)`, archivedAt: new Date().toLocaleDateString('de-DE') }, ...loadedHistory];
                            needsSave = true;
                            return false; 
                        }
                        return true; 
                    });
                    setClearingItems(activeItems);
                    setHistoryItems(loadedHistory);
                    if (needsSave) setIsDirty(true); 
                }
                setIsClearingLoading(false);

                const archiveRef = doc(db, 'system_data', 'invoices_archive');
                const archiveSnap = await getDoc(archiveRef);
                if (archiveSnap.exists()) setArchiveItems(archiveSnap.data().invoices || []);
                setIsArchiveLoading(false);
            } catch (error) {
                console.error("Fehler beim Laden der Systemdaten", error);
                setIsClearingLoading(false);
                setIsArchiveLoading(false);
            }
        };
        loadSystemData();
    }, []);

    useEffect(() => {
        if (!currentUserId) return;
        // Coaches: immer nur eigene Belege (auth.uid).
        // Master-Admin: Global = alles; Filter = gewählter Coach.
        const queryId = isGlobalView
            ? null
            : (isAdminUser && filteredCoachMember?.id)
                ? String(filteredCoachMember.id)
                : currentUserId;
        
        // 💉 CHIRURGIE-FIX: Serverseitig gefilterte Query für Coaches (Permission-sicher),
        // nur die Global-Ansicht abonniert die gesamte Collection. Sortierung bleibt lokal (kein Index nötig).
        const invoicesRef = queryId
            ? query(collection(db, 'coach_manual_invoices'), where('coachId', '==', queryId))
            : collection(db, 'coach_manual_invoices');

        const unsubscribe = onSnapshot(invoicesRef, (snapshot) => {
            let docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            
            // Lokaler Filter (Sicherheitsnetz)
            if (queryId) {
                docs = docs.filter((d: any) => d.coachId === queryId);
            }
            
            // Lokale Sortierung
            docs.sort((a: any, b: any) => {
                const getTime = (val: any) => {
                    if (!val) return 0;
                    if (val.toMillis) return val.toMillis(); 
                    if (typeof val === 'string') return new Date(val).getTime(); 
                    return 0;
                };
                return getTime(b.createdAt) - getTime(a.createdAt);
            });

            setClientInvoices(docs);
            setIsClientInvoicesLoading(false);
        }, (error) => {
            // 💉 Stiller Fallback: Permission-Fehler (z.B. Coach ohne Rechnungen/Rechte) blockiert das Dashboard nicht mehr
            console.warn("Coach Invoices Listener: Zugriff nicht möglich, zeige leere Liste.", error?.code || error);
            setClientInvoices([]);
            setIsClientInvoicesLoading(false);
        });
        
        return () => unsubscribe();
    }, [currentUserId, isGlobalView, isAdminUser, filteredCoachMember?.id]);

    useEffect(() => {
        if (!isDirty || isClearingLoading) return;
        const timer = setTimeout(() => {
            const saveToFirebase = async () => {
                try {
                    const docRef = doc(db, 'system_data', 'clearing');
                    await setDoc(docRef, { clearingItems, historyItems, lastUpdated: new Date().toISOString() }, { merge: true });
                    setIsDirty(false);
                } catch (error) {}
            };
            saveToFirebase();
        }, 1500); 
        return () => clearTimeout(timer);
    }, [clearingItems, historyItems, isDirty, isClearingLoading]);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => { if (user) fetchClients(); });
        return () => unsubscribe();
    }, [fetchClients]);

    const handleLogout = () => auth.signOut();

    const deleteArchiveInvoice = async (invoiceId: string, invoiceNumber: string) => {
        const input = window.prompt(`WARNUNG: Willst du die offiziell gespeicherte Rechnung ${invoiceNumber} wirklich LÖSCHEN?\n\nDamit wird sie aus der Datenbank entfernt.\n\nBitte "LÖSCHEN" exakt so eintippen zur Bestätigung:`);
        if (input === 'LÖSCHEN') {
            try {
                const archiveRef = doc(db, 'system_data', 'invoices_archive');
                const archiveSnap = await getDoc(archiveRef);
                if (archiveSnap.exists()) {
                    const currentArchive = archiveSnap.data().invoices || [];
                    const updatedArchive = currentArchive.filter((inv: any) => inv.id !== invoiceId);
                    
                    await setDoc(archiveRef, { invoices: updatedArchive }, { merge: true });
                    setArchiveItems(updatedArchive); 
                    alert(`Rechnung ${invoiceNumber} wurde restlos vernichtet.`);
                }
            } catch (error) {
                console.error("Fehler beim Löschen:", error);
                alert("Fehler beim Löschen aus dem Archiv.");
            }
        } else if (input !== null) {
            alert("Abbruch: Falsches Bestätigungswort.");
        }
    };

    const getRevenueSplit = useCallback((client: any, coachProfile: any) => {
        if (!client || !coachProfile || !coachProfile.id) return 0;
        if (!clientMatchesMember(client, coachProfile, team, teamRegistry)) return 0;
        if (coachProfile.billingFactor) return getAmount(coachProfile.billingFactor) / 100;
        return 1.0;
    }, [team, teamRegistry]);

    const calculateClearingEffect = useCallback((item: any, coachProfile: any) => {
        if (!item || !coachProfile) return 0;
        const viewerName = String(coachProfile.name).trim().toLowerCase();
        const splitKey = Object.keys(TEAM_SPLIT || {}).find(k => {
            const kLower = String(k).trim().toLowerCase();
            return viewerName.includes(kLower) || kLower.includes(viewerName);
        }) || "";
        const splitShare = TEAM_SPLIT[coachProfile.name] || TEAM_SPLIT[splitKey] || 0; 
        const itemAssigned = String(item.assignedTo || '').trim().toLowerCase();
        const isOriginator = itemAssigned.includes(viewerName) || viewerName.includes(itemAssigned) || itemAssigned === String(coachProfile.id).toLowerCase();
        const val = getAmount(item.amountStr);

        if (item.type === 'cost') {
            const reimbursement = isOriginator ? val : 0;
            const costShare = val * splitShare;
            return reimbursement - costShare;
        } else {
            const debt = isOriginator ? -val : 0;
            const revenueShare = val * splitShare;
            return debt + revenueShare;
        }
    }, [TEAM_SPLIT]);

    const realClients = useMemo(() => {
        const seenIds = new Set<string>();
        const mergedSource: any[] = [];
        [...(crmClients || []), ...(pendingApprovals || [])].forEach((c) => {
            if (!c?.id || seenIds.has(c.id)) return;
            seenIds.add(c.id);
            mergedSource.push(c);
        });

        return mergedSource.filter(c => c && c.isArchived !== true && c.isDeleted !== true && c.isActive !== false).map(c => {
            const { vatType, vatRate } = extractVatInfo(c);
            const vorname = c.vorname || '';
            const nachname = c.nachname || '';
            const firmenname = String(c.firmenname || c.clientData?.salonname || c.clientData?.firmenname || c.salonname || 'Keine Firma').trim();
            
            const fullName = `${c.vorname || ''} ${c.nachname || ''}`.trim()
                || (firmenname !== 'Keine Firma' ? firmenname : '')
                || '';

            const years = c.geschaeftsjahre || [];
            const latestStatus = years.length > 0 ? years[years.length - 1].status : 'datenerfassung';
            const stripeBadge = resolveStripeBillingBadge(c, { latestYearStatus: latestStatus });
            const isProBono = c.proBonoActive || c.clientData?.proBonoActive;
            const stripeDisplay = isProBono || stripeBadge.kind === 'pro_bono' ? null : stripeBadge.display;
            const stripeColor = stripeBadge.className;
            const stripeCritical = stripeBadge.isCritical;
            const proBonoUntil = c.proBonoUntil || c.clientData?.proBonoUntil;
            const fixedFee = c.monthlyFee ?? c.clientData?.monthlyFee;
            const hasFixedFee = fixedFee !== undefined && fixedFee !== null && fixedFee !== '';
            const premiumTools = resolvePremiumToolOwnerBilling(c);

            return {
                id: c.id, 
                firmenname, 
                vorname, 
                nachname, 
                fullName,
                mandantNr: c.mandantNr || '---',
                customerNumber: c.customerNumber || c.clientData?.customerNumber || '',
                packageTier: c.packageTier || c.clientData?.packageTier || '',
                tarifStatus: c.tarifStatus || c.clientData?.tarifStatus || '',
                billingStatus: c.billingStatus || c.clientData?.billingStatus || '',
                isExistingCustomer: c.isExistingCustomer ?? c.clientData?.isExistingCustomer,
                isBestandskunde: c.isBestandskunde ?? c.clientData?.isBestandskunde,
                hasEmployeeEval: c.hasEmployeeEval ?? c.clientData?.hasEmployeeEval,
                isSolo: c.isSolo ?? c.clientData?.isSolo,
                vertragsbeginn: c.vertragsbeginn || c.clientData?.vertragsbeginn || '',
                betreuer: c.betreuer || c.clientData?.betreuer || c.coachId || c.clientData?.coachId || c.coach || 'Unzugewiesen', 
                email: c.email || c.clientData?.mailadresse || '',
                monthlyRevenue: calculateMonthlyFee(c, globalSettings),
                premiumToolSoll: premiumTools.sollMonthly,
                premiumToolRevenue: premiumTools.billedMonthly,
                premiumToolProBono: premiumTools.proBono, 
                vatRate, 
                vatType, 
                stripeDisplay, 
                stripeColor,
                stripeCritical,
                latestStatus, 
                kuendigungsDatum: c.kuendigungsDatum || null, 
                createdAt: c.createdAt || null,
                clientData: c.clientData || null, 
                isProBono, 
                proBonoUntil, 
                fixedFee, 
                hasFixedFee,
                isLegacy: c.isLegacy || false,
                isArchived: c.isArchived || false, 
                land: c.land || c.clientData?.land || 'Deutschland',
                lastActiveAt: c.lastActiveAt || c.clientData?.lastActiveAt || null,
                snoozedUntil: c.snoozedUntil || null,
            };
        }).sort((a, b) => a.fullName.localeCompare(b.fullName, 'de', { sensitivity: 'base' })); 
    }, [crmClients, pendingApprovals, globalSettings]);

    const computeCoachBaseFinancials = useCallback((coachProfile: any) => {
        if (!coachProfile) {
            return {
                profile: {},
                myClients: [] as any[],
                revShare: 0,
                fixedSalary: 0,
                managementBonus: 0,
                clearing: 0,
                pepulseMalusBase: 0,
            };
        }

        const fixedSalary = getAmount(coachProfile.fixedSalary || 0);
        const myClients = realClients.filter((c) => getRevenueSplit(c, coachProfile) > 0);
        const revShare = myClients.reduce(
            (sum, c) => sum + c.monthlyRevenue * getRevenueSplit(c, coachProfile),
            0
        );
        const clearing = (clearingItems || []).reduce(
            (acc, curr) => acc + calculateClearingEffect(curr, coachProfile),
            0
        );

        let managementBonus = 0;
        const viewerNameLower = String(coachProfile.name).toLowerCase();
        if (viewerNameLower.includes('martina')) {
            let totalRestForBonus = 0;
            activeTeam.forEach((m: any) => {
                if (!m) return;
                const lowerName = String(m.name || '').toLowerCase();
                if (lowerName.includes('jean') || lowerName.includes('nelly')) {
                    const theirClients = realClients.filter((c) => getRevenueSplit(c, m) > 0);
                    const theirTotalRev = theirClients.reduce((sum, c) => sum + c.monthlyRevenue, 0);
                    const theirRevShare = theirClients.reduce(
                        (sum, c) => sum + c.monthlyRevenue * getRevenueSplit(c, m),
                        0
                    );
                    const theirClearing = (clearingItems || []).reduce(
                        (acc, curr) => acc + calculateClearingEffect(curr, m),
                        0
                    );
                    const theirFixed = getAmount((m as any).fixedSalary || 0);
                    const theirFinalPayout = theirRevShare + theirFixed + theirClearing;
                    totalRestForBonus += theirTotalRev - theirFinalPayout;
                }
            });
            managementBonus = totalRestForBonus > 0 ? totalRestForBonus * 0.35 : 0;
        }

        const pepulseMalusBase = revShare + fixedSalary + managementBonus + clearing;
        return {
            profile: coachProfile,
            myClients,
            revShare,
            fixedSalary,
            managementBonus,
            clearing,
            pepulseMalusBase,
        };
    }, [realClients, activeTeam, clearingItems, getRevenueSplit, calculateClearingEffect]);

    /** Pass 1: Malus-Pool aus allen Team-Mitgliedern (Billing-Abzug), Pass 2: 50/50 GF */
    const pepulseRedistribHalf = useMemo(() => {
        if (!isDeductionActive) return 0;
        let pool = 0;
        const teamPool = mergeTeamPool(team, teamRegistry);
        teamPool.forEach((m: any) => {
            if (!m?.email) return;
            const email = String(m.email).trim().toLowerCase();
            const category = String(m.category || '').toLowerCase();
            const role = String(m.role || '').toLowerCase();
            if (category === 'support' || role === 'support') return;
            if (isBillingMalusExemptEmail(email)) return;
            const isEligible =
                ['coach', 'admin', 'superadmin'].includes(category) ||
                ['coach', 'admin', 'superadmin'].includes(role);
            if (!isEligible) return;
            const base = computeCoachBaseFinancials(m).pepulseMalusBase;
            const points = Number(pepulseM1PointsByEmail[email] || 0);
            const vacation = clampVacationDays(pepulseM1VacationByEmail[email] || 0);
            const target = targetPointsFromVacation(vacation);
            const percent = achievementPercent(points, target);
            const rate = billingMalusRate(percent, email);
            if (rate > 0 && base > 0) {
                pool += (base * rate) / 100;
            }
        });
        return pool / 2;
    }, [
        team,
        teamRegistry,
        computeCoachBaseFinancials,
        pepulseM1PointsByEmail,
        pepulseM1VacationByEmail,
        isDeductionActive,
    ]);

    const getCoachFinancials = useCallback((coachProfile: any) => {
        if (!coachProfile) {
            return {
                profile: {},
                myClients: [],
                revShare: 0,
                fixedSalary: 0,
                managementBonus: 0,
                clearing: 0,
                totalPayout: 0,
                pepulseMalus: 0,
                pepulseMalusRate: 0,
                pepulseDisplayMalusRate: 0,
                pepulsePoints: 0,
                pepulseMalusBase: 0,
                pepulseTargetPoints: SOCIAL_MEDIA_MONTHLY_TARGET_POINTS,
                pepulseVacationDays: 0,
                pepulsePrevMonth,
                pepulseRedistributionBonus: 0,
            };
        }

        const baseFin = computeCoachBaseFinancials(coachProfile);
        const email = String(coachProfile.email || '').trim().toLowerCase();
        const category = String(coachProfile.category || '').toLowerCase();
        const role = String(coachProfile.role || '').toLowerCase();
        const isSupport = category === 'support' || role === 'support';
        const isPepulseEligible =
            !isSupport &&
            (isBillingMalusExemptEmail(email) ||
                ['coach', 'admin', 'superadmin'].includes(category) ||
                ['coach', 'admin', 'superadmin'].includes(role));

        const pepulsePoints = email ? Number(pepulseM1PointsByEmail[email] || 0) : 0;
        const pepulseVacationDays = email
            ? clampVacationDays(pepulseM1VacationByEmail[email] || 0)
            : 0;
        const pepulseTargetPoints = targetPointsFromVacation(pepulseVacationDays);
        const percent = achievementPercent(pepulsePoints, pepulseTargetPoints);

        const pepulseDisplayMalusRate = isPepulseEligible
            ? displayMalusRate(percent, email)
            : 0;
        const pepulseBillingRate = isPepulseEligible
            ? billingMalusRate(percent, email)
            : 0;
        const pepulseMalusBase = baseFin.pepulseMalusBase;
        const pepulseMalus = isPepulseEligible
            ? billingMalusEuro(pepulseMalusBase, percent, email, isDeductionActive)
            : 0;

        const pepulseRedistributionBonus =
            isBillingMalusExemptEmail(email) && pepulseRedistribHalf > 0
                ? pepulseRedistribHalf
                : 0;

        const totalPayout =
            pepulseMalusBase + pepulseMalus + pepulseRedistributionBonus;

        return {
            ...baseFin,
            totalPayout,
            pepulseMalus,
            pepulseMalusRate: pepulseBillingRate,
            pepulseDisplayMalusRate,
            pepulsePoints,
            pepulseMalusBase,
            pepulseTargetPoints,
            pepulseVacationDays,
            pepulsePrevMonth,
            pepulseRedistributionBonus,
        };
    }, [
        computeCoachBaseFinancials,
        pepulseM1PointsByEmail,
        pepulseM1VacationByEmail,
        pepulsePrevMonth,
        pepulseRedistribHalf,
        isDeductionActive,
    ]);

    const handleGenerateInvoices = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!window.confirm("Bist du sicher, dass du die Monatsabrechnung JETZT für alle Coaches generieren willst? Dieser Vorgang ist endgültig.")) return;
        setIsGeneratingInvoices(true);
        setInvoiceSuccessMsg('');

        try {
            const archiveRef = doc(db, 'system_data', 'invoices_archive');
            const archiveSnap = await getDoc(archiveRef);
            let currentArchive: any[] = [];
            if (archiveSnap.exists()) {
                currentArchive = archiveSnap.data().invoices || [];
            }

            const today = new Date();
            const issueDateStr = today.toLocaleDateString('de-DE');
            const targetMonthText = today.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
            const shortMonth = String(today.getMonth() + 1).padStart(2, '0');
            const yearStr = today.getFullYear().toString();

            const newInvoices: any[] = [];

            activeTeam.forEach((m: any) => {
                if (!m || isOwnerProfile(m.name, m.email)) return;
                
                const d = getCoachFinancials(m);
                const coachInitial = String(m.name).charAt(0).toUpperCase();
                const invNumber = `K${coachInitial}${shortMonth}${yearStr}`; 

                newInvoices.push({
                    id: `inv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                    invoiceNumber: invNumber,
                    issueDate: issueDateStr,
                    targetMonthText: targetMonthText,
                    coachId: m.id,
                    coachName: m.name,
                    coachEmail: m.email,
                    coach: m, 
                    revenueNet: d.revShare + d.fixedSalary,
                    managementBonus: d.managementBonus,
                    clearingTotal: d.clearing,
                    supervisedClientCount: (d.myClients || []).length,
                    pepulseMalus: d.pepulseMalus || 0,
                    pepulseMalusRate: d.pepulseMalusRate || 0,
                    pepulseDisplayMalusRate: d.pepulseDisplayMalusRate || 0,
                    pepulsePoints: d.pepulsePoints || 0,
                    pepulseMalusBase: d.pepulseMalusBase || 0,
                    pepulseTargetPoints: d.pepulseTargetPoints || SOCIAL_MEDIA_MONTHLY_TARGET_POINTS,
                    pepulseVacationDays: d.pepulseVacationDays || 0,
                    pepulsePrevMonth: d.pepulsePrevMonth || pepulsePrevMonth,
                    pepulseRedistributionBonus: d.pepulseRedistributionBonus || 0,
                    createdAt: new Date().toISOString()
                });
            });

            const filteredArchive = currentArchive.filter((inv: any) => inv.targetMonthText !== targetMonthText);
            const updatedArchive = [...newInvoices, ...filteredArchive];

            await setDoc(archiveRef, { invoices: updatedArchive }, { merge: true });

            setArchiveItems(updatedArchive);
            setInvoiceSuccessMsg('✅ Abrechnung erfolgreich korrigiert & generiert!');
            setTimeout(() => setInvoiceSuccessMsg(''), 5000);

            } catch (error) {
                console.error("Fehler beim lokalen Generieren der Rechnungen:", error);
                alert("Es gab einen Fehler bei der Abrechnung. Bitte Console prüfen.");
            } finally {
                setIsGeneratingInvoices(false);
            }
    };

    const deleteClientInvoice = async (invoiceId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        if(!window.confirm("Möchtest du diese Rechnung wirklich dauerhaft aus dem System löschen?")) return;
        try {
            await deleteDoc(doc(db, 'coach_manual_invoices', invoiceId));
            if(selectedHistoryInvoice?.id === invoiceId) setSelectedHistoryInvoice(null);
        } catch(error) {
            console.error("Fehler beim Löschen:", error);
            alert("Fehler beim Löschen der Rechnung.");
        }
    };

    /** Millisekunden seit lastActiveAt — null wenn kein Timestamp vorhanden. */
    const getInactiveMs = (client: any): number | null => {
        const raw = client.lastActiveAt;
        if (!raw) return null;
        const ts = typeof raw === 'number' ? raw
            : raw?.seconds ? raw.seconds * 1000
            : raw?.toDate ? raw.toDate().getTime()
            : typeof raw === 'string' ? new Date(raw).getTime()
            : null;
        if (!ts || isNaN(ts)) return null;
        return Date.now() - ts;
    };

    const isClientSnoozed = (client: any): boolean => {
        const su = client.snoozedUntil;
        if (!su) return false;
        const ts = typeof su === 'number' ? su
            : su?.seconds ? su.seconds * 1000
            : su?.toDate ? su.toDate().getTime()
            : typeof su === 'string' ? new Date(su).getTime()
            : 0;
        return ts > Date.now();
    };

    // ONLINE-Tab: CRM periodisch neu laden — lastActiveAt kommt sonst nur einmalig beim Mount
    useEffect(() => {
        if (radarFilter !== 'online') return;
        void fetchClients();
        const t = window.setInterval(() => {
            void fetchClients();
        }, 60 * 1000);
        return () => window.clearInterval(t);
    }, [radarFilter, fetchClients]);

    const coachClientCountMap = useMemo(() => {
        const map: Record<string, number> = {};
        coachDropdownTeam.forEach((m: any) => {
            if (!m?.id) return;
            map[m.id] = realClients.filter((client) =>
                clientMatchesMember(client, m, team, teamRegistry)
            ).length;
        });
        return map;
    }, [coachDropdownTeam, realClients, team, teamRegistry]);

    const displayCrmClients = useMemo(() => {
        /** Puffer über Heartbeat-Intervall (2 Min) — sonst wirkt ONLINE leer. */
        const MS_ONLINE = 20 * 60 * 1000;
        const MS_60DAYS = 60 * 24 * 60 * 60 * 1000;
        const MS_90DAYS = 90 * 24 * 60 * 60 * 1000;
        return realClients.filter(client => {
            if (adminFilter !== 'Alle') {
                if (!filteredCoachMember?.id) return false;
                if (!clientMatchesMember(client, filteredCoachMember, team, teamRegistry)) return false;
            } else if (showOnlyMine) {
                if (!filterMemberProfile?.id) return false;
                if (!clientMatchesMember(client, filterMemberProfile, team, teamRegistry)) return false;
            }
            if (searchTerm.trim() !== '') {
                const term = searchTerm.toLowerCase();
                const firmennameMatch = String(client.firmenname || '').toLowerCase().includes(term);
                const mandantNrMatch = String(client.mandantNr || '').toLowerCase().includes(term);
                const nameMatch = client.fullName.toLowerCase().includes(term);
                if (!firmennameMatch && !mandantNrMatch && !nameMatch) return false;
            }

            // Radar-Filter
            if (radarFilter !== 'all') {
                const inactiveMs = getInactiveMs(client);
                if (radarFilter === 'online') {
                    if (inactiveMs === null || inactiveMs > MS_ONLINE) return false;
                } else if (radarFilter === 'inactive60') {
                    if (inactiveMs !== null && inactiveMs < MS_60DAYS) return false;
                    // null (nie aktiv) gilt als inaktiv
                } else if (radarFilter === 'acute90') {
                    if (isClientSnoozed(client)) return false;
                    if (inactiveMs !== null && inactiveMs < MS_90DAYS) return false;
                    // null (nie aktiv) gilt als akut inaktiv
                }
            }

            return true; 
        }); 
    }, [realClients, adminFilter, filteredCoachMember, showOnlyMine, searchTerm, filterMemberProfile, team, teamRegistry, radarFilter]);

    const toggleSection = (section: keyof typeof sections) => setSections(prev => ({ ...prev, [section]: !prev[section] }));

    const archiveItem = (id: string) => {
        const itemToArchive = clearingItems.find(i => i.id === id);
        if (!itemToArchive) return;
        setClearingItems(prev => prev.filter(i => i.id !== id));
        setHistoryItems(prev => [{ ...itemToArchive, archivedAt: new Date().toLocaleDateString('de-DE') }, ...prev]);
        setIsDirty(true);
    };

    const handleAddClearingItem = () => {
        setClearingItems([{ id: `c_${Date.now()}`, type: 'cost', interval: 'einmalig', desc: 'Neuer Posten', amountStr: '', assignedTo: targetCoachName, targetMonth: getCurrentMonthTag() }, ...clearingItems]);
        setIsDirty(true);
    };

    const handleUpdateClearingItem = (id: string, field: string, value: any) => {
        setClearingItems(prev => prev.map(item => item.id === id ? { ...item, [field]: value } : item));
        setIsDirty(true);
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => {
        if (e.target.tagName === 'INPUT') (e.target as HTMLInputElement).select();
    };

    const openLivePrognosisFor = (coachProfile: any) => {
        const data = getCoachFinancials(coachProfile);
        if (isOwnerProfile(String((data.profile as any).name), String((data.profile as any).email))) {
            alert("Fehler: Als Inhaber können Sie sich keine Honorarabrechnung erstellen.");
            return;
        }
        const today = new Date();
        const targetMonthText = today.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
        setPreviewInvoiceData({
            coach: data.profile,
            invoiceNumber: 'PROGNOSE (Nicht offiziell)',
            issueDate: 'LIVE-KALKULATION',
            targetMonthText: targetMonthText,
            revenueNet: data.revShare + data.fixedSalary, 
            managementBonus: data.managementBonus,
            clearingTotal: data.clearing,
            supervisedClientCount: (data.myClients || []).length,
            pepulseMalus: data.pepulseMalus || 0,
            pepulseMalusRate: data.pepulseMalusRate || 0,
            pepulseDisplayMalusRate: data.pepulseDisplayMalusRate || 0,
            pepulsePoints: data.pepulsePoints || 0,
            pepulseMalusBase: data.pepulseMalusBase || 0,
            pepulseTargetPoints: data.pepulseTargetPoints || SOCIAL_MEDIA_MONTHLY_TARGET_POINTS,
            pepulseVacationDays: data.pepulseVacationDays || 0,
            pepulsePrevMonth: data.pepulsePrevMonth || pepulsePrevMonth,
            pepulseRedistributionBonus: data.pepulseRedistributionBonus || 0,
        });
        setIsInvoiceExpanded(true);
    };

    const openArchivedInvoice = (snapshotData: any) => {
        let count = snapshotData?.supervisedClientCount;
        if (typeof count !== 'number' && snapshotData?.coach) {
            const d = getCoachFinancials(snapshotData.coach);
            count = (d?.myClients || []).length;
        }
        setPreviewInvoiceData({
            ...snapshotData,
            supervisedClientCount: count,
        });
        setIsInvoiceExpanded(true);
    };

    // --- ABRECHNUNG PER E-MAIL (mail_queue -> processMailQueue mit PDF-Anhang) ---
    const [isSendingInvoiceMail, setIsSendingInvoiceMail] = useState(false);
    const [isExportingPdf, setIsExportingPdf] = useState(false);
    const [bulkMailStatus, setBulkMailStatus] = useState<{ month: string; done: number; total: number } | null>(null);

    const resolveInvoiceCoachEmail = (inv: any): string => {
        const direct = String(inv?.coach?.email || inv?.coachEmail || '').trim();
        if (direct) return direct;
        const coachId = inv?.coachId || inv?.coach?.id;
        const byId = coachId ? resolveMemberById(String(coachId), fullTeam) : null;
        if (byId?.email) return String(byId.email).trim();
        const coachName = String(inv?.coach?.name || inv?.coachName || '').trim().toLowerCase();
        if (coachName) {
            const byName = fullTeam.find(
                (m: any) => String(m?.name || '').trim().toLowerCase() === coachName
            );
            if (byName?.email) return String(byName.email).trim();
        }
        return '';
    };

    const normalizeArchiveInvoice = (invoice: any) => {
        const revNet = Number(invoice?.revenueNet || invoice?.netAmount || invoice?.revShare || 0);
        const mBonus = Number(invoice?.managementBonus || 0);
        const cTotal = Number(invoice?.clearingTotal || invoice?.clearing || 0);
        const displayCoach = invoice?.coachName || invoice?.coach?.name || '---';
        return {
            ...invoice,
            coach: invoice?.coach || {
                name: displayCoach,
                email: invoice?.coachEmail || '',
                id: invoice?.coachId,
            },
            invoiceNumber: invoice?.invoiceNumber || '---',
            issueDate: invoice?.issueDate || invoice?.createdAt?.substring?.(0, 10) || '---',
            targetMonthText: invoice?.targetMonthText || invoice?.targetMonth || 'UNBEKANNT',
            revenueNet: revNet,
            managementBonus: mBonus,
            clearingTotal: cTotal,
        };
    };

    const buildCoachInvoicePdf = async (
        invoiceNr?: string,
        invoiceOverride?: any
    ): Promise<{ pdf: jsPDF; fileName: string; base64PDF: string; isEInvoice: boolean }> => {
        const data = invoiceOverride || previewInvoiceData;
        if (!data) throw new Error('Keine Rechnungsdaten.');

        const pdf = await buildCoachHonorariumPdfDoc({
            coach: data.coach,
            invoiceNumber: String(invoiceNr || data.invoiceNumber || ''),
            issueDate: String(data.issueDate || ''),
            targetMonthText: String(data.targetMonthText || ''),
            revenueNet: Number(data.revenueNet || 0),
            managementBonus: Number(data.managementBonus || 0),
            clearingTotal: Number(data.clearingTotal || 0),
            supervisedClientCount:
                typeof data.supervisedClientCount === 'number'
                    ? data.supervisedClientCount
                    : data.coach
                        ? (getCoachFinancials(data.coach)?.myClients || []).length
                        : undefined,
            pepulseMalus: Number(data.pepulseMalus || 0),
            pepulseMalusRate: Number(data.pepulseMalusRate || 0),
            pepulsePoints: Number(data.pepulsePoints || 0),
            pepulseMalusBase: Number(data.pepulseMalusBase || 0),
            pepulseTargetPoints: Number(data.pepulseTargetPoints || 0),
            pepulseVacationDays: Number(data.pepulseVacationDays || 0),
            pepulsePrevMonth: String(data.pepulsePrevMonth || ''),
            pepulseRedistributionBonus: Number(data.pepulseRedistributionBonus || 0),
        });

        const safeNr = String(invoiceNr || data.invoiceNumber || 'Mandant').replace(/[^\w.-]+/g, '_');
        const coachObj = (data.coach || {}) as Record<string, unknown>;
        const rawCoachName = String(
            coachObj.name || data.coachName || 'Coach'
        ).trim();
        const safeCoachName = rawCoachName.replace(/[^\w.-]+/g, '_').replace(/^_+|_+$/g, '') || 'Coach';
        let fileName = `Rechnung_${safeNr}_${safeCoachName}.pdf`;
        let base64PDF = pdf.output('datauristring');
        let isEInvoice = false;

        // Seller = Coach des Belegs (PDF-Visual), NICHT das Profil des eingeloggten Admins.
        const sellerProfile = coachMemberToEInvoiceProfile(
            (data.coach || {}) as Record<string, unknown>
        );
        const sellerReady = assessCoachEInvoiceProfile(sellerProfile).eInvoiceReady;

        if (sellerReady) {
            try {
                const net = Number(data.revenueNet || 0)
                    + Number(data.managementBonus || 0)
                    + Number(data.clearingTotal || 0)
                    + Number(data.pepulseMalus || 0)
                    + Number(data.pepulseRedistributionBonus || 0);
                const reverseCharge = sellerProfile.reverseCharge === true
                    || data?.coach?.invoiceReverseCharge === true;
                const vatTotal = reverseCharge ? 0 : net * 0.19;
                const grossTotal = net + vatTotal;
                const invoiceDateIso = new Date().toISOString().split('T')[0];
                const zugferd = buildCoachHonorariumZugferdPayload({
                    profile: sellerProfile,
                    invoiceNumber: String(invoiceNr || data.invoiceNumber || safeNr),
                    invoiceDateIso,
                    targetMonthText: String(data.targetMonthText || ''),
                    netTotal: net,
                    vatTotal,
                    grossTotal,
                    reverseCharge,
                });
                const hybrid = await attachZugferdToPdfBytes(pdf.output('arraybuffer'), zugferd);
                base64PDF = hybrid.base64PDF;
                fileName = fileName.replace('.pdf', '_E-Rechnung.pdf');
                isEInvoice = true;
                return { pdf, fileName, base64PDF, isEInvoice };
            } catch (err) {
                console.warn('ZUGFeRD-Anhang fehlgeschlagen — normales PDF:', err);
            }
        }

        return { pdf, fileName, base64PDF, isEInvoice };
    };

    const enqueueCoachAbrechnungMail = async (inv: any, base64PDF: string, email: string) => {
        const coachFirstName = String(inv?.coach?.name || inv?.coachName || 'Team').split(' ')[0];
        const monthLabel = String(inv?.targetMonthText || '').trim();
        const subject = `Honorar-Abrechnung ${monthLabel}`.trim();
        const messageHtml = [
            `<p>Liebe/r ${coachFirstName},</p>`,
            `<p>anbei sende ich Euch die Abrechnungen für den oben genannten Monat. Bitte schaut kurz drüber, ob alles für Euch so vollständig und richtig ist.</p>`,
            `<p>Vielen Dank für Eure tolle Unterstützung! Ich freue mich schon auf den nächsten Monat mit Euch.</p>`,
        ].join('');

        await addDoc(collection(db, 'mail_queue'), {
            type: 'coach_abrechnung',
            email,
            participantName: coachFirstName,
            eventName: subject,
            targetMonthText: monthLabel,
            subject,
            messageHtml,
            invoiceNr: inv?.invoiceNumber || 'ohne-nr',
            pdfBase64: base64PDF,
            isCoachInvoice: true,
            status: 'pending',
            createdAt: serverTimestamp(),
        });
    };

    const handlePdfDownload = async () => {
        if (!previewInvoiceData || isExportingPdf || isSendingInvoiceMail) return;
        try {
            setIsExportingPdf(true);
            const { pdf, fileName, base64PDF, isEInvoice } = await buildCoachInvoicePdf();
            if (isEInvoice && base64PDF.startsWith('data:')) {
                const raw = atob(base64PDF.split(',')[1] || '');
                const bytes = new Uint8Array(raw.length);
                for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
                triggerPdfBytesDownload(bytes, fileName);
            } else {
                pdf.save(fileName);
            }
        } catch (error) {
            console.error('PDF-Export fehlgeschlagen:', error);
            alert('Fehler beim Erstellen der PDF.');
        } finally {
            setIsExportingPdf(false);
        }
    };

    const handleSendInvoiceMail = async () => {
        if (!previewInvoiceData || isSendingInvoiceMail || isExportingPdf) return;
        if (previewInvoiceData.issueDate === 'LIVE-KALKULATION') {
            alert('Eine Live-Prognose kann nicht versendet werden. Bitte zuerst die offizielle Monatsabrechnung generieren.');
            return;
        }
        const email = resolveInvoiceCoachEmail(previewInvoiceData);
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!email || !emailRegex.test(email)) {
            alert('Keine gültige E-Mail-Adresse gefunden.\n\nBitte hinterlege die Adresse des Team-Mitglieds in den globalen Daten.');
            return;
        }
        if (!window.confirm(`Abrechnung ${previewInvoiceData.invoiceNumber || ''} jetzt per E-Mail an ${email} senden?`)) return;
        try {
            setIsSendingInvoiceMail(true);
            const { base64PDF } = await buildCoachInvoicePdf(previewInvoiceData.invoiceNumber);
            await enqueueCoachAbrechnungMail(previewInvoiceData, base64PDF, email);
            alert(`✅ E-Mail-Auftrag übergeben — die Abrechnung geht in wenigen Sekunden an ${email}.`);
        } catch (error) {
            console.error('Abrechnung per Mail fehlgeschlagen:', error);
            alert('Fehler: Die Abrechnung konnte nicht versendet werden.');
        } finally {
            setIsSendingInvoiceMail(false);
        }
    };

    /** Monats-Sammelversand: ein mail_queue-Job pro Beleg — Backend unverändert. */
    const handleSendMonthMailsToAll = async (month: string, rawItems: any[]) => {
        if (isSendingInvoiceMail || isExportingPdf || bulkMailStatus) return;
        const items = (rawItems || []).filter(Boolean);
        if (items.length === 0) {
            alert('Keine Belege in diesem Monat.');
            return;
        }
        if (!window.confirm(
            `Alle ${items.length} Honorarabrechnungen für „${month}“ jetzt per E-Mail an die hinterlegten Adressen senden?\n\nEinzelsendung bleibt weiterhin über „Beleg öffnen“ möglich.`
        )) return;

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        setIsSendingInvoiceMail(true);
        setBulkMailStatus({ month, done: 0, total: items.length });
        let ok = 0;
        const errors: string[] = [];

        try {
            for (const raw of items) {
                const inv = normalizeArchiveInvoice(raw);
                const label = `${inv.coach?.name || inv.coachName || 'Coach'} (${inv.invoiceNumber || 'ohne-nr'})`;
                const email = resolveInvoiceCoachEmail(inv);

                if (!email || !emailRegex.test(email)) {
                    errors.push(`${label}: keine gültige E-Mail`);
                    setBulkMailStatus((prev) => prev ? { ...prev, done: prev.done + 1 } : prev);
                    continue;
                }

                try {
                    const { base64PDF } = await buildCoachInvoicePdf(inv.invoiceNumber, inv);
                    await enqueueCoachAbrechnungMail(inv, base64PDF, email);
                    ok += 1;
                } catch (err) {
                    console.error('Monatsversand Einzelbeleg fehlgeschlagen:', label, err);
                    errors.push(`${label}: Versand fehlgeschlagen`);
                }
                setBulkMailStatus((prev) => prev ? { ...prev, done: prev.done + 1 } : prev);
            }

            const summary = `Monatsversand „${month}“: ${ok} von ${items.length} erfolgreich übergeben.`
                + (errors.length ? `\n\nHinweise:\n• ${errors.join('\n• ')}` : '');
            alert(summary);
        } finally {
            setIsSendingInvoiceMail(false);
            setBulkMailStatus(null);
        }
    };

    const cleanArchiveItems = useMemo(() => {
        return archiveItems.filter((i: any) => {
            if (!i) return false;
            const cName = String(i.coachName || i.coach?.name || i.name || '');
            const cEmail = String(i.coachEmail || i.coach?.email || i.email || '');
            return !isOwnerProfile(cName, cEmail);
        });
    }, [archiveItems]);
    
    const visibleArchiveItems = useMemo(() => {
        return (isGlobalView ? cleanArchiveItems : cleanArchiveItems.filter((i: any) => String(i?.coachName || '').toLowerCase() === targetCoachName.toLowerCase()))
            .sort((a: any, b: any) => String(b?.createdAt || '').localeCompare(String(a?.createdAt || '')));
    }, [cleanArchiveItems, isGlobalView, targetCoachName]);

    // 💉 ARCHIV: Logik für die nach Monaten gruppierte Ziehharmonika
    const groupedArchivesArray = useMemo(() => {
        const map = new Map<string, any[]>();
        visibleArchiveItems.forEach((item: any) => {
            const month = item.targetMonthText || 'Unbekannt';
            if(!map.has(month)) map.set(month, []);
            map.get(month)!.push(item);
        });
        return Array.from(map.entries()).map(([month, items]) => ({ month, items }));
    }, [visibleArchiveItems]);

    // 💉 ARCHIV: Klappt den neuesten Monat (den ersten) automatisch auf
    useEffect(() => {
        if (groupedArchivesArray.length > 0 && Object.keys(expandedArchiveMonths).length === 0) {
            setExpandedArchiveMonths({ [groupedArchivesArray[0].month]: true });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupedArchivesArray]);

    const toggleArchiveMonth = (month: string) => {
        setExpandedArchiveMonths(prev => ({ ...prev, [month]: !prev[month] }));
    };

    const activeClientInvoicePreviewData = useMemo(() => {
        if (!selectedHistoryInvoice) return null;
        const fallbackClient = selectedHistoryInvoice.client || realClients.find(c => c.id === selectedHistoryInvoice.clientId);
        const coachBase =
            selectedHistoryInvoice.coach ||
            (previewCoachProfileWithEInvoice as Record<string, unknown>);
        return {
            ...selectedHistoryInvoice,
            client: fallbackClient,
            coach: applyCoachEInvoiceToMemberFields(coachBase, coachEInvoiceProfile),
        };
    }, [
        selectedHistoryInvoice,
        realClients,
        previewCoachProfileWithEInvoice,
        coachEInvoiceProfile,
    ]);

    const handleEditInvoice = (inv: any) => {
        if (!inv) return;
        setEditingClientInvoice(inv);
        setSelectedHistoryInvoice(inv);
        setInvoiceView('create');
    };

    const closeClientInvoiceModal = () => {
        setEditingClientInvoice(null);
        setInvoiceView('list');
    };

    const downloadClientManualInvoice = async (invoice: any) => {
        if (!invoice) return;
        try {
            setSelectedHistoryInvoice(invoice);
            setIsCreatingClientInvoice(true);
            await new Promise(r => setTimeout(r, 600)); 

            const printArea = document.getElementById('client-manual-print-factory');
            const element = printArea?.querySelector('.invoice-print-container') as HTMLElement;
            if (!element) throw new Error("Print Factory nicht gefunden.");

            const canvas = await html2canvas(element, { scale: 2, useCORS: true, logging: false, backgroundColor: '#ffffff' });
            const pdf = new jsPDF('p', 'mm', 'a4');
            const imgData = canvas.toDataURL('image/jpeg', 1.0);
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
            pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);

            const safeName = String(invoice.clientName || 'Mandant').replace(/\s+/g, '_');
            let fileName = `Rechnung_${invoice.invoiceNumber || 'Manuell'}_${safeName}.pdf`;

            const sellerProfile = coachMemberToEInvoiceProfile(
                (invoice.coach || previewCoachProfile || {}) as Record<string, unknown>
            );
            const sellerReady = assessCoachEInvoiceProfile(sellerProfile).eInvoiceReady;

            if (sellerReady) {
                try {
                    const net = Number(invoice.netAmount || invoice.net || 0);
                    const reverseCharge =
                        invoice.reverseCharge === true ||
                        (typeof invoice.reverseCharge !== 'boolean' && Number(invoice.vatRate) === 0);
                    const vatRate = reverseCharge ? 0 : 19;
                    const vatTotal = reverseCharge ? 0 : net * 0.19;
                    const grossTotal = net + vatTotal;
                    const customer = buildClientBuyerCustomer({
                        companyName: invoice.clientFirma || invoice.clientCompany || invoice.clientName,
                        fullName: invoice.clientName,
                        email: invoice.clientEmail || invoice.recipientEmail,
                        street: invoice.clientStrasse,
                        zip: invoice.clientPlz,
                        city: invoice.clientOrt,
                        country: invoice.clientLand,
                        clientData: invoice.clientData || invoice.client?.clientData,
                    });
                    const zugferd = buildCoachClientZugferdPayload({
                        profile: sellerProfile,
                        customer,
                        invoiceNumber: String(invoice.invoiceNumber || 'Manuell'),
                        invoiceDateIso: new Date().toISOString().split('T')[0],
                        description: String(invoice.title || invoice.invoiceTitle || 'Consulting / Coaching'),
                        netTotal: net,
                        vatTotal,
                        grossTotal,
                        vatRate,
                        reverseCharge,
                    });
                    const hybrid = await attachZugferdToPdfBytes(pdf.output('arraybuffer'), zugferd);
                    fileName = fileName.replace('.pdf', '_E-Rechnung.pdf');
                    triggerPdfBytesDownload(hybrid.pdfBytes, fileName);
                    return;
                } catch (err) {
                    console.warn('Mandanten-E-Rechnung fehlgeschlagen — normales PDF:', err);
                }
            }

            pdf.save(fileName);
        } catch (error) {
            console.error(error);
            alert("Fehler beim PDF Download.");
        } finally {
            setIsCreatingClientInvoice(false);
        }
    };

    /** Mandanten-/Fremdkunden-Rechnung per mail_queue — Schema 1:1 wie AdminInvoiceCreator (Seminar) */
    const sendClientManualInvoiceMail = async (invoice: any, recipientEmailOverride?: string) => {
        if (!invoice || isSendingClientInvoiceMail || isCreatingClientInvoice) return;

        const crmEmail = String(
            realClients.find((c: any) => c.id === invoice.clientId)?.email
            || realClients.find((c: any) => c.id === invoice.clientId)?.clientData?.mailadresse
            || ''
        ).trim();
        // Priorität: 1) expliziter Override (UI) 2) gespeicherte Empfänger-Mail 3) Snapshot 4) CRM-Fallback
        const recipientEmail = String(
            (recipientEmailOverride && recipientEmailOverride.trim())
            || invoice.recipientEmail
            || invoice.clientEmail
            || crmEmail
            || ''
        ).trim();

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!recipientEmail || !emailRegex.test(recipientEmail)) {
            alert('Keine gültige Empfänger-E-Mail. Bitte Adresse prüfen (CRM oder manuell).');
            return;
        }

        if (
            !window.confirm(
                `Rechnung wirklich an diese Adresse senden?\n\n${recipientEmail}\n\n(Gespeicherte Empfänger-Mail hat Vorrang vor CRM.)`
            )
        ) {
            return;
        }

        try {
            setIsSendingClientInvoiceMail(true);
            // DOM muss die Print-Factory mit dieser Rechnung rendern (wie setPreviewIndex beim Seminar)
            flushSync(() => {
                setSelectedHistoryInvoice(invoice);
            });
            await new Promise((r) => setTimeout(r, 600));

            const printArea = document.getElementById('client-manual-print-factory');
            const element = printArea?.querySelector('.invoice-print-container') as HTMLElement;
            if (!element) throw new Error('Print Factory nicht gefunden.');

            const canvas = await html2canvas(element, {
                scale: 2,
                useCORS: true,
                logging: false,
                backgroundColor: '#ffffff',
            });
            const pdf = new jsPDF('p', 'mm', 'a4');
            const imgData = canvas.toDataURL('image/jpeg', 1.0);
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
            pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);

            // E-Rechnung (ZUGFeRD) — Seller = Coach des Belegs, nicht Auth-User
            let base64PDF = pdf.output('datauristring');
            const sellerProfile = coachMemberToEInvoiceProfile(
                (invoice.coach || previewCoachProfile || {}) as Record<string, unknown>
            );
            if (assessCoachEInvoiceProfile(sellerProfile).eInvoiceReady) {
                try {
                    const net = Number(invoice.netAmount || invoice.net || 0);
                    const reverseCharge =
                        invoice.reverseCharge === true ||
                        (typeof invoice.reverseCharge !== 'boolean' && Number(invoice.vatRate) === 0);
                    const vatRate = reverseCharge ? 0 : 19;
                    const vatTotal = reverseCharge ? 0 : net * 0.19;
                    const grossTotal = net + vatTotal;
                    const customer = buildClientBuyerCustomer({
                        companyName: invoice.clientFirma || invoice.clientCompany || invoice.clientName,
                        fullName: invoice.clientName,
                        email: recipientEmail,
                        street: invoice.clientStrasse,
                        zip: invoice.clientPlz,
                        city: invoice.clientOrt,
                        country: invoice.clientLand,
                        clientData: invoice.clientData || invoice.client?.clientData,
                    });
                    const zugferd = buildCoachClientZugferdPayload({
                        profile: sellerProfile,
                        customer,
                        invoiceNumber: String(invoice.invoiceNumber || 'Manuell'),
                        invoiceDateIso: new Date().toISOString().split('T')[0],
                        description: String(invoice.title || invoice.invoiceTitle || 'Consulting / Coaching'),
                        netTotal: net,
                        vatTotal,
                        grossTotal,
                        vatRate,
                        reverseCharge,
                    });
                    const hybrid = await attachZugferdToPdfBytes(pdf.output('arraybuffer'), zugferd);
                    base64PDF = hybrid.base64PDF;
                } catch (err) {
                    console.warn('Mandanten-E-Rechnung (Mail) fehlgeschlagen — normales PDF:', err);
                }
            }

            const participantName = String(
                invoice.clientName || invoice.clientFirma || 'Kunde'
            ).trim();
            const invoiceNr = String(invoice.invoiceNumber || 'ohne-nr').trim();
            const eventName = String(
                invoice.invoiceTitle || invoice.clientFirma || 'Consulting / Coaching'
            ).trim();

            // Absender für Antworten: ausführender Coach (nicht System-From)
            const replyTo = String(
                authUser?.email
                || currentUserProfile?.email
                || invoice.coachEmail
                || ''
            ).trim();

            // Queue-Schema wie Seminar + replyTo (Worker setzt Reply-To-Header)
            await addDoc(collection(db, 'mail_queue'), {
                email: recipientEmail.trim(),
                participantName,
                eventName,
                eventLocation: '',
                isOnline: false,
                onlineLink: '',
                invoiceNr,
                pdfBase64: base64PDF,
                appBaseUrl: window.location.origin,
                ...(replyTo ? { replyTo } : {}),
                status: 'pending',
                createdAt: serverTimestamp(),
            });

            alert('✅ E-Mail-Auftrag wurde erfolgreich an den Server übergeben! Er wird in wenigen Sekunden gesendet.');
        } catch (error) {
            console.error('sendClientManualInvoiceMail fehlgeschlagen:', error);
            alert('Fehler: Konnte E-Mail-Auftrag nicht erstellen.');
        } finally {
            setIsSendingClientInvoiceMail(false);
        }
    };

    return (
        <div className="min-h-screen bg-pe-bg text-pe-text font-sans flex flex-col relative overflow-x-hidden print:bg-white print:text-black">
            
            {invoiceView === 'create' && (
                <CoachInvoiceModal
                    isOpen={true}
                    editingInvoice={editingClientInvoice}
                    onClose={closeClientInvoiceModal}
                />
            )}

            {/* Standalone /pepulse: schlanker Mobile-Header ohne Portal-Ballast */}
            {isPepulseStandalone ? (
                <header className="w-full border-b border-pe-border bg-pe-elevated sticky top-0 z-40 print:hidden">
                    <div className="px-4 h-14 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 min-w-0">
                            <span className="text-pe-accent-text font-black tracking-widest uppercase text-lg leading-none">
                                PEPulse
                            </span>
                        </div>
                        <button
                            onClick={handleLogout}
                            className="flex items-center gap-2 px-3 py-1.5 bg-pe-card hover:bg-pe-hover rounded text-[10px] font-black tracking-widest text-pe-muted border border-pe-border-strong whitespace-nowrap"
                        >
                            <LogOutIcon size={14} /> VERLASSEN
                        </button>
                    </div>
                </header>
            ) : (
            <header className="w-full border-b border-pe-border bg-pe-elevated sticky top-0 z-40 shadow-[0_4px_20px_rgba(0,0,0,0.5)] print:hidden">
                <div className="max-w-[1600px] mx-auto px-4 md:px-8 h-auto md:h-28 py-3 md:py-0 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4 md:gap-6 min-w-0">
                        <div className="flex flex-col justify-center sm:border-l border-pe-border-strong sm:pl-6 h-12">
                            <div className="text-xs font-black tracking-[0.3em] uppercase text-pe-muted mb-1">PreisExpert</div>
                            <span className="text-pe-accent-text font-black tracking-widest uppercase text-xl leading-none">Coach Portal</span>
                        </div>
                    </div>
                    <div className="hidden md:flex items-center gap-4 lg:gap-6 flex-nowrap shrink-0">
                        {isAdminUser && (
                            <div className="flex items-center gap-2">
                                <span className="text-xs font-bold text-pe-muted uppercase tracking-widest whitespace-nowrap">Ansicht:</span>
                                <select
                                    value={adminFilter}
                                    onChange={(e) => { setAdminFilter(e.target.value); setIsInvoiceExpanded(false); }}
                                    className="bg-pe-input dark:bg-pe-bg border border-[#E6C299]/50 text-pe-accent-text text-sm font-bold px-3 py-2 rounded outline-none focus:border-[#E6C299] focus:ring-1 focus:ring-[#E6C299] text-center cursor-pointer appearance-none shadow-sm"
                                >
                                    <option value="Alle">Global ({realClients.length} Mandanten)</option>
                                    {coachDropdownTeam.map((m: any) => (
                                        m ? <option key={m.id} value={m.id}>{m.name} ({coachClientCountMap[m.id] ?? 0} Mandanten)</option> : null
                                    ))}
                                </select>
                            </div>
                        )}
                        <ProductivityDock
                            showNotification={(msg) => window.alert(msg)}
                            syncStatus="idle"
                            isAdminView={true}
                        />
                        
                        {isAdminUser && (
                            <button onClick={() => window.location.replace('/')} className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded text-sm font-black tracking-widest transition-colors border border-red-400 shadow-lg whitespace-nowrap">
                                <ShieldAlertIcon size={16} /> ADMIN-PANEL
                            </button>
                        )}
                        
                        <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2 bg-pe-card hover:bg-pe-hover rounded text-xs font-black tracking-widest text-pe-muted border border-pe-border-strong whitespace-nowrap">
                            <LogOutIcon size={16} /> VERLASSEN
                        </button>
                    </div>

                    {/* MOBILE-REFORM: Hamburger-Menü, nur < 768px sichtbar */}
                    <button
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        className="md:hidden flex-shrink-0 p-2 rounded-lg border border-pe-border-strong dark:border-pe-border-strong bg-pe-card dark:bg-pe-card text-pe-accent-text hover:bg-pe-hover dark:hover:bg-pe-hover transition-colors"
                        aria-label="Menü öffnen/schließen"
                    >
                        {isMobileMenuOpen ? <XLucideIcon size={22} /> : <MenuLucideIcon size={22} />}
                    </button>
                </div>

                {/* Mobil: Dock immer sichtbar */}
                <div className="md:hidden px-3 pb-3 border-t border-pe-border">
                    <ProductivityDock
                        showNotification={(msg) => window.alert(msg)}
                        syncStatus="idle"
                        isAdminView={true}
                    />
                </div>

                {/* MOBILE-REFORM: Einklappbares Navigations-Panel (Spiegel der Desktop-Sidebar), nur < 768px */}
                {isMobileMenuOpen && (
                    <div className="md:hidden border-t border-pe-border px-4 py-4 space-y-1 animate-fade-in bg-pe-elevated max-h-[min(70vh,32rem)] overflow-y-auto overscroll-contain">
                        <button onClick={() => { setActiveTab('crm'); setIsMobileMenuOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border font-medium transition-colors ${activeTab === 'crm' ? 'bg-pe-hover dark:bg-pe-card text-pe-text border-pe-border-strong dark:border-pe-border-strong' : 'text-pe-muted dark:text-pe-muted hover:bg-pe-hover dark:hover:bg-pe-hover hover:text-pe-text border-transparent'}`}>
                            <UsersIcon size={18} className={activeTab === 'crm' ? 'text-pe-accent-text' : ''} /> Mandanten-Akte
                        </button>
                        {canAccessPepulse && (
                            <button onClick={() => { setActiveTab('pepulse'); setIsMobileMenuOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border font-medium transition-colors ${activeTab === 'pepulse' ? 'bg-pe-hover dark:bg-pe-card text-pe-text border-pe-border-strong dark:border-pe-border-strong' : 'text-pe-muted dark:text-pe-muted hover:bg-pe-hover dark:hover:bg-pe-hover hover:text-pe-text border-transparent'}`}>
                                <ActivityIcon size={18} className={activeTab === 'pepulse' ? 'text-pe-accent-text' : ''} /> PEPulse
                            </button>
                        )}
                        <button onClick={() => { setActiveTab('finanzen'); setIsMobileMenuOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border font-medium transition-colors ${activeTab === 'finanzen' ? 'bg-pe-hover dark:bg-pe-card text-pe-text border-pe-border-strong dark:border-pe-border-strong' : 'text-pe-muted dark:text-pe-muted hover:bg-pe-hover dark:hover:bg-pe-hover hover:text-pe-text border-transparent'}`}>
                            <BriefcaseIcon size={18} className={activeTab === 'finanzen' ? 'text-pe-accent-text' : ''} /> Finanz-Tresor
                        </button>
                        <button onClick={() => { setActiveTab('rechnungen'); setIsMobileMenuOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border font-medium transition-colors ${activeTab === 'rechnungen' ? 'bg-pe-hover dark:bg-pe-card text-pe-text border-pe-border-strong dark:border-pe-border-strong' : 'text-pe-muted dark:text-pe-muted hover:bg-pe-hover dark:hover:bg-pe-hover hover:text-pe-text border-transparent'}`}>
                            <FileTextIcon size={18} className={activeTab === 'rechnungen' ? 'text-pe-accent-text' : ''} /> Rechnungs-Center
                        </button>
                        <button onClick={() => { setShowIntakeHub(true); setIsMobileMenuOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border font-medium transition-colors ${openIntakeCount ? 'bg-red-600 text-white border-red-500 hover:bg-red-500' : pendingAdoptionCount ? 'bg-amber-950/50 text-amber-200 border-amber-500/50 hover:bg-amber-900/40' : 'text-pe-accent-text border-[#E6C299]/40 hover:bg-pe-hover dark:hover:bg-pe-hover'}`}>
                            <UsersIcon size={18} className={openIntakeCount ? 'text-pe-text' : pendingAdoptionCount ? 'text-amber-300' : 'text-pe-accent-text'} /> Neukunden
                            <span className="ml-auto flex items-center gap-1.5">
                              <IntakePendingAdoptionPill count={pendingAdoptionCount} />
                              <IntakeCountPill count={openIntakeCount} />
                            </span>
                        </button>

                        {isAdminUser && (
                            <>
                                <div className="my-3 border-t border-pe-border"></div>
                                <div className="flex items-center gap-3 px-4 py-2">
                                    <span className="text-xs font-bold text-pe-muted uppercase tracking-widest flex-shrink-0">Ansicht:</span>
                                    <select 
                                        value={adminFilter}
                                        onChange={(e) => { setAdminFilter(e.target.value); setIsInvoiceExpanded(false); }}
                                        className="w-full bg-pe-input dark:bg-pe-bg border border-[#E6C299]/50 text-pe-accent-text text-sm font-bold px-4 py-2 rounded outline-none focus:border-[#E6C299] focus:ring-1 focus:ring-[#E6C299] text-center cursor-pointer appearance-none shadow-sm"
                                    >
                                        <option value="Alle">Global ({realClients.length} Mandanten)</option>
                                        {coachDropdownTeam.map((m: any) => (
                                            m ? <option key={m.id} value={m.id}>{m.name} ({coachClientCountMap[m.id] ?? 0} Mandanten)</option> : null
                                        ))}
                                    </select>
                                </div>
                                <button onClick={() => window.location.replace('/')} className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-600 hover:bg-red-500 text-white rounded-lg text-sm font-black tracking-widest transition-colors border border-red-400 shadow-lg">
                                    <ShieldAlertIcon size={16} /> ADMIN-PANEL
                                </button>
                            </>
                        )}

                        <div className="pt-2 border-t border-pe-border">
                            <button onClick={handleLogout} className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-pe-card hover:bg-pe-hover rounded-lg text-xs font-black tracking-widest text-pe-muted border border-pe-border-strong">
                                <LogOutIcon size={16} /> VERLASSEN
                            </button>
                        </div>
                    </div>
                )}
            </header>
            )}

            <div className={`flex-1 flex flex-col md:flex-row max-w-[1600px] w-full mx-auto print:hidden ${isPepulseStandalone ? 'p-3' : 'p-4 md:p-8 gap-8'}`}>
                {!isPepulseStandalone && (
                <aside className="w-64 flex-shrink-0 hidden md:flex md:flex-col sticky top-28 self-start">
                    <div className="space-y-2 flex-1">
                        <button onClick={() => setActiveTab('crm')} className={`w-full flex items-center justify-center gap-3 px-4 py-3 rounded-lg border font-medium transition-colors ${activeTab === 'crm' ? 'bg-pe-hover dark:bg-pe-card text-pe-text border-pe-border-strong dark:border-pe-border-strong' : 'text-pe-muted dark:text-pe-muted hover:bg-pe-hover dark:hover:bg-pe-hover hover:text-pe-text border-transparent'}`}>
                            <UsersIcon size={20} className={activeTab === 'crm' ? 'text-pe-accent-text' : ''} /> Mandanten-Akte
                        </button>
                        {canAccessPepulse && (
                            <button onClick={() => setActiveTab('pepulse')} className={`w-full flex items-center justify-center gap-3 px-4 py-3 rounded-lg border font-medium transition-colors ${activeTab === 'pepulse' ? 'bg-pe-hover dark:bg-pe-card text-pe-text border-pe-border-strong dark:border-pe-border-strong' : 'text-pe-muted dark:text-pe-muted hover:bg-pe-hover dark:hover:bg-pe-hover hover:text-pe-text border-transparent'}`}>
                                <ActivityIcon size={20} className={activeTab === 'pepulse' ? 'text-pe-accent-text' : ''} /> PEPulse
                            </button>
                        )}
                        <button onClick={() => setActiveTab('finanzen')} className={`w-full flex items-center justify-center gap-3 px-4 py-3 rounded-lg border font-medium transition-colors ${activeTab === 'finanzen' ? 'bg-pe-hover dark:bg-pe-card text-pe-text border-pe-border-strong dark:border-pe-border-strong' : 'text-pe-muted dark:text-pe-muted hover:bg-pe-hover dark:hover:bg-pe-hover hover:text-pe-text border-transparent'}`}>
                            <BriefcaseIcon size={20} className={activeTab === 'finanzen' ? 'text-pe-accent-text' : ''} /> Finanz-Tresor
                        </button>
                        <button onClick={() => setActiveTab('rechnungen')} className={`w-full flex items-center justify-center gap-3 px-4 py-3 rounded-lg border font-medium transition-colors ${activeTab === 'rechnungen' ? 'bg-pe-hover dark:bg-pe-card text-pe-text border-pe-border-strong dark:border-pe-border-strong' : 'text-pe-muted dark:text-pe-muted hover:bg-pe-hover dark:hover:bg-pe-hover hover:text-pe-text border-transparent'}`}>
                            <FileTextIcon size={20} className={activeTab === 'rechnungen' ? 'text-pe-accent-text' : ''} /> Rechnungs-Center
                        </button>
                        <button onClick={() => setShowIntakeHub(true)} className={`w-full flex items-center justify-center gap-3 px-4 py-3 rounded-lg border font-medium transition-colors ${openIntakeCount ? 'bg-red-600 text-white border-red-500 hover:bg-red-500' : pendingAdoptionCount ? 'bg-amber-950/50 text-amber-200 border-amber-500/50 hover:bg-amber-900/40' : 'text-pe-accent-text border-[#E6C299]/40 hover:bg-pe-hover dark:hover:bg-pe-hover'}`}>
                            <UsersIcon size={20} className={openIntakeCount ? 'text-pe-text' : pendingAdoptionCount ? 'text-amber-300' : 'text-pe-accent-text'} /> Neukunden
                            <span className="ml-auto flex items-center gap-1.5">
                              <IntakePendingAdoptionPill count={pendingAdoptionCount} />
                              <IntakeCountPill count={openIntakeCount} />
                            </span>
                        </button>
                    </div>

                    {/* Radar in der Sidebar */}
                    <div className="mt-4 pt-4 border-t border-pe-border space-y-3">
                        <SentinelRadar />
                        <StripeRadar />
                        <AktivCounterRadar />
                        <ChangeRadarBadge isAdmin={isAdminUser} />
                    </div>
                </aside>
                )}

                <main className="flex-1 min-w-0 w-full">
                    {showMissingTopicReminder && (
                        <div className="mb-6 bg-amber-950/40 border border-amber-500/40 rounded-xl p-4 flex items-start gap-3 print:hidden">
                            <AlertTriangleIcon size={18} className="text-amber-400 flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="text-sm font-bold text-amber-300">Noch kein Monats-Thema hinterlegt</p>
                                <p className="text-[11px] text-amber-200/70 mt-1 leading-relaxed">
                                    Mindestens ein Event steht in den nächsten 10 Tagen an, hat aber noch kein Thema.
                                    Bitte Monatsinhalt einpflegen, sonst bleibt die Events-Seite ohne Themenblock.
                                </p>
                            </div>
                        </div>
                    )}
                    {/* /pepulse Isolation: nur P-Pulse PWA, kein CRM/Finanzen/Rechnungen */}
                    {isPepulseStandalone ? (
                        canAccessPepulse ? (
                            <div className="animate-fade-in">
                                <MobileTrackerView
                                    isAdmin={isAdminUser}
                                    payoutBaseByEmail={Object.fromEntries(
                                        (activeTeam || [])
                                            .filter((m: any) => m && m.email)
                                            .map((m: any) => {
                                                const d = getCoachFinancials(m);
                                                return [
                                                    String(m.email).trim().toLowerCase(),
                                                    Number(d.pepulseMalusBase || 0),
                                                ];
                                            })
                                    )}
                                />
                            </div>
                        ) : (
                            <div className="rounded-2xl border border-pe-border bg-pe-card p-6 text-lg text-pe-muted">
                                PEPulse ist nur für Coach und Admin verfügbar.
                            </div>
                        )
                    ) : (
                    <>
                    {/* TAB 1: CRM */}
                    {activeTab === 'crm' && (
                        <div className="animate-fade-in">
                            <CoachSeminarOverview />
                            <div className="mb-8 flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
                                <div>
                                    <h1 className="text-3xl font-bold text-pe-text tracking-tight mb-2 flex flex-wrap items-center gap-3">
                                        <span>Mandanten Betreuung</span>
                                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black uppercase tracking-widest bg-[#E6C299]/15 text-pe-accent-text border border-[#E6C299]/40 shadow-sm">
                                            <UsersIcon size={14} />
                                            {adminFilter !== 'Alle'
                                                ? `${displayCrmClients.length} ${displayCrmClients.length === 1 ? 'Mandant' : 'Mandanten'} (${targetCoachName})`
                                                : showOnlyMine
                                                    ? `${displayCrmClients.length} ${displayCrmClients.length === 1 ? 'Mandant' : 'Mandanten'} (Nur Meine)`
                                                    : `${displayCrmClients.length} ${displayCrmClients.length === 1 ? 'Mandant' : 'Mandanten'} gesamt`}
                                        </span>
                                    </h1>
                                    <p className="text-pe-muted">
                                        Verwalte deine zugewiesenen Salons und springe in deren Kalkulations-Akte.
                                    </p>
                                </div>
                                <div className="flex flex-col md:flex-row items-center gap-4 w-full md:w-auto mt-4 md:mt-0">
                                    {isAdminUser && (
                                        <div className="flex items-center gap-3 w-full md:w-auto">
                                            <span className="text-xs font-bold text-pe-muted uppercase tracking-widest whitespace-nowrap">Ansicht:</span>
                                            <select
                                                value={adminFilter}
                                                onChange={(e) => { setAdminFilter(e.target.value); setIsInvoiceExpanded(false); }}
                                                className="w-full md:w-auto bg-pe-input dark:bg-pe-bg border border-[#E6C299]/50 text-pe-accent-text text-sm font-bold px-4 py-2 rounded outline-none focus:border-[#E6C299] focus:ring-1 focus:ring-[#E6C299] text-center cursor-pointer appearance-none shadow-sm"
                                            >
                                                <option value="Alle">Global ({realClients.length} Mandanten)</option>
                                                {coachDropdownTeam.map((m: any) => (
                                                    m ? <option key={m.id} value={m.id}>{m.name} ({coachClientCountMap[m.id] ?? 0} Mandanten)</option> : null
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                    <div className="relative w-full md:w-64">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <SearchIcon size={14} className="text-pe-muted" />
                                        </div>
                                        <input 
                                            type="text" 
                                            placeholder="Name oder Mandant-Nr..." 
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="w-full pl-9 pr-4 py-2 bg-pe-input dark:bg-pe-input border border-pe-border-strong dark:border-pe-border-strong rounded text-sm text-pe-text focus:border-[#E6C299] outline-none transition-colors placeholder-stone-400 dark:placeholder:text-pe-muted"
                                        />
                                    </div>
                                    <button 
                                        onClick={() => setShowOnlyMine(!showOnlyMine)}
                                        className={`w-full md:w-auto flex justify-center items-center gap-2 px-4 py-2 rounded border text-xs font-bold uppercase tracking-widest transition-all ${showOnlyMine ? 'bg-[#E6C299]/10 border-[#E6C299] text-pe-accent-text shadow-[0_0_15px_rgba(230,194,153,0.2)]' : 'bg-pe-card dark:bg-pe-card border-pe-border-strong dark:border-pe-border-strong text-pe-muted dark:text-pe-muted hover:bg-pe-hover dark:hover:bg-pe-hover'}`}
                                    >
                                        <FilterIcon size={14} /> {showOnlyMine ? 'Nur Meine' : 'Alle zeigen'}
                                    </button>
                                </div>
                            </div>
                            {/* ── Inaktivitäts-Radar Filter-Tabs ── */}
                            <div className="flex flex-wrap items-center gap-2 mb-4">
                                {([
                                    ['all', 'Alle'],
                                    ['online', '🟢 Online'],
                                    ['inactive60', '⏳ Inaktiv (> 60 Tage)'],
                                    ['acute90', '🔴 Akut (> 90 Tage)'],
                                ] as const).map(([key, label]) => (
                                    <button
                                        key={key}
                                        onClick={() => setRadarFilter(key)}
                                        className={`px-3 py-1.5 rounded-lg border text-[10px] font-bold uppercase tracking-widest transition-all ${
                                            radarFilter === key
                                                ? key === 'acute90'
                                                    ? 'bg-red-500/15 border-red-500/60 text-red-400 shadow-[0_0_10px_rgba(239,68,68,0.15)]'
                                                    : key === 'inactive60'
                                                        ? 'bg-amber-100 border-amber-500 text-amber-950 dark:bg-amber-500/15 dark:border-amber-500/60 dark:text-amber-400 shadow-[0_0_10px_rgba(245,158,11,0.15)]'
                                                        : key === 'online'
                                                            ? 'bg-emerald-500/15 border-emerald-500/60 text-emerald-400 shadow-[0_0_10px_rgba(16,185,129,0.15)]'
                                                            : 'bg-[#E6C299]/10 border-[#E6C299] text-pe-accent-text'
                                                : 'bg-pe-card dark:bg-pe-card border-pe-border-strong dark:border-pe-border-strong text-pe-muted dark:text-pe-muted hover:text-pe-text'
                                        }`}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>

                            <div className="bg-pe-card rounded-xl border border-pe-border shadow-xl overflow-hidden">
                                {isLoadingPending ? (
                                    <div className="p-8 text-center text-pe-muted animate-pulse font-bold uppercase tracking-widest">Lade Akten...</div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left border-collapse min-w-[600px]">
                                            <thead>
                                                <tr className="bg-pe-tile-header border-b border-pe-border text-xs uppercase tracking-wider text-pe-muted">
                                                    <th className="p-4 font-black">Mandant & Salon</th>
                                                    <th className="p-4 font-black">System-ID & Coach</th>
                                                    <th className="p-4 font-black">Status</th>
                                                    <th className="p-4 font-black text-right">Aktion</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-pe-border">
                                                {displayCrmClients.map((client) => {
                                                    const coachObj = resolveClientCoach(client, team, teamRegistry);
                                                    const coachDisplayName = coachObj ? coachObj.name : 'Unzugewiesen';
                                                    const isMyClient = getRevenueSplit(client, previewCoachProfile) > 0;
                                                    
                                                    const isDeathRow = !!client.kuendigungsDatum;
                                                    const cancelDateObj = isDeathRow ? new Date(client.kuendigungsDatum!) : null;
                                                    const tarifBadge = resolveTarifBadgeDisplay({
                                                        mandantNr: client.mandantNr,
                                                        client: {
                                                            ...client,
                                                            packageTier: client.packageTier,
                                                            tarifStatus: client.tarifStatus,
                                                            hasEmployeeEval: client.hasEmployeeEval,
                                                            isSolo: client.isSolo,
                                                        },
                                                        settings,
                                                        isLegacy: !!(client.isLegacy || client.clientData?.isLegacy),
                                                        isSolo: typeof client.isSolo === 'boolean' ? client.isSolo : undefined,
                                                        hasEmployeeEval: typeof client.hasEmployeeEval === 'boolean' ? client.hasEmployeeEval : undefined,
                                                        forceSoloTarif: !!(client.forceSoloTarif || client.clientData?.forceSoloTarif),
                                                    });

                                                    const vertragsbeginnRaw =
                                                        resolveVertragsbeginnDisplay(client) ||
                                                        (typeof client.vertragsbeginn === 'string' &&
                                                        /^\d{2}\/\d{4}$/.test(client.vertragsbeginn.trim())
                                                            ? client.vertragsbeginn.trim()
                                                            : null) ||
                                                        (typeof client.clientData?.vertragsbeginn === 'string' &&
                                                        /^\d{2}\/\d{4}$/.test(String(client.clientData.vertragsbeginn).trim())
                                                            ? String(client.clientData.vertragsbeginn).trim()
                                                            : null);
                                                    let laufzeitText = "Laufzeit: Startdatum fehlt";
                                                    
                                                    if (vertragsbeginnRaw && vertragsbeginnRaw.includes('/')) {
                                                        const parts = vertragsbeginnRaw.split('/');
                                                        if (parts.length === 2) {
                                                            const startMonth = parseInt(parts[0], 10);
                                                            const startYear = parseInt(parts[1], 10);
                                                            
                                                            if (!isNaN(startMonth) && !isNaN(startYear) && startMonth >= 1 && startMonth <= 12) {
                                                                const now = new Date();
                                                                const currentMonth = now.getMonth() + 1;
                                                                const currentYear = now.getFullYear();
                                                                let nextRenewalYear = currentYear;
                                                                if (currentMonth >= startMonth) nextRenewalYear++;
                                                                if (startYear >= nextRenewalYear) nextRenewalYear = startYear + 1;
                                                                const paddedMonth = startMonth.toString().padStart(2, '0');
                                                                laufzeitText = `Start: ${paddedMonth}/${startYear} | Nächste Verlängerung: ${paddedMonth}/${nextRenewalYear}`;
                                                            }
                                                        }
                                                    }

                                                    const rawNr = String(client.mandantNr || '').trim();
                                                    const kdNr = String(client.customerNumber || '').trim();
                                                    const isLegacyRow = !!(client.isLegacy || client.clientData?.isLegacy);
                                                    const coachLetter = resolveCoachInitialLetter(
                                                        coachObj?.name || client.betreuer || client.coachId,
                                                        team
                                                    );
                                                    const looksVorab =
                                                        isLegacyRow ||
                                                        isVorabOrLegacyMandantNr(rawNr) ||
                                                        isVorabOrLegacyMandantNr(kdNr) ||
                                                        /^MA\s+\d{1,2}\s*[./-]\s*\d{4}/i.test(rawNr) ||
                                                        /^MA\s+\d{1,2}\s*[./-]\s*\d{4}/i.test(kdNr);
                                                    const systemNr =
                                                        rawNr &&
                                                        rawNr !== 'WIEDERHERGESTELLT' &&
                                                        rawNr !== '---'
                                                            ? (looksVorab
                                                                ? (normalizeVorabMandantNr(rawNr, coachLetter) ||
                                                                   formatMandantNrDisplay(rawNr, coachLetter) ||
                                                                   rawNr)
                                                                : (formatMandantNrDisplay(rawNr, coachLetter) || rawNr))
                                                            : '';
                                                    const kdClean =
                                                        kdNr && kdNr !== 'PENDING'
                                                            ? (looksVorab
                                                                ? (normalizeVorabMandantNr(kdNr, coachLetter) ||
                                                                   formatMandantNrDisplay(kdNr, coachLetter) ||
                                                                   kdNr)
                                                                : (formatMandantNrDisplay(kdNr, coachLetter) || kdNr))
                                                            : '';
                                                    // Vorab: normalisierte System-ID zuerst
                                                    const displayKundenNr = looksVorab
                                                        ? (systemNr || kdClean || 'System-ID wird gesetzt…')
                                                        : (kdClean || systemNr || 'System-ID wird gesetzt…');
                                                    const kdPending = !kdNr || kdNr === 'PENDING';
                                                    const isBillingPaused =
                                                        client.billingStatus === 'pending_approval' ||
                                                        kdNr === 'PENDING' ||
                                                        rawNr === 'WIEDERHERGESTELLT' ||
                                                        (client.monthlyRevenue === 0 &&
                                                            (client.isExistingCustomer === false ||
                                                                client.isBestandskunde === false));
                                                    
                                                    return (
                                                        <tr key={client.id} className={`transition-colors group ${isDeathRow ? 'bg-orange-900/10 border-l-2 border-l-orange-500 hover:bg-orange-900/20' : 'hover:bg-pe-hover'}`}>
                                                            <td className="p-4">
                                                                <div className="font-bold text-pe-text text-base leading-tight flex items-center gap-2">
                                                                    {client.fullName}
                                                                    {client.isLegacy && (
                                                                        <span className="bg-blue-100 text-blue-900 border border-blue-400 dark:bg-blue-500/10 dark:text-blue-400 dark:border-blue-500/30 px-1.5 py-0.5 rounded text-[8px] uppercase tracking-widest font-black shrink-0">Vorabkunde</span>
                                                                    )}
                                                                </div>
                                                                
                                                                {isDeathRow && cancelDateObj && (
                                                                    <div className="text-[9px] font-black tracking-widest text-orange-400 bg-orange-500/10 border border-orange-500/20 px-2 py-0.5 rounded mt-1 mb-1.5 inline-block">
                                                                        GEKÜNDIGT ZUM: {cancelDateObj.toLocaleDateString('de-DE')}
                                                                    </div>
                                                                )}

                                                                <div className="text-xs text-pe-accent-text font-bold mb-1.5 uppercase tracking-wide mt-1">{client.firmenname}</div>
                                                                <div className="text-[10px] text-pe-muted font-mono flex items-center gap-1.5 mt-1">
                                                                    <MailIcon size={10} className="text-pe-muted" /> {client.email || '---'}
                                                                </div>
                                                                {/* ── Skool-Style Aktivitäts-Badge ── */}
                                                                {(() => {
                                                                    const snoozed = isClientSnoozed(client);
                                                                    if (snoozed) {
                                                                        const suRaw = client.snoozedUntil;
                                                                        const suTs = typeof suRaw === 'number' ? suRaw : suRaw?.seconds ? suRaw.seconds * 1000 : suRaw?.toDate ? suRaw.toDate().getTime() : typeof suRaw === 'string' ? new Date(suRaw).getTime() : 0;
                                                                        const untilStr = suTs ? new Date(suTs).toLocaleDateString('de-DE') : '?';
                                                                        return (
                                                                            <div className="flex items-center gap-1.5 mt-1.5 text-[9px] text-blue-800 dark:text-blue-400 font-bold">
                                                                                <span className="inline-block w-2 h-2 rounded-full bg-blue-400/60 shrink-0" />
                                                                                Pausiert bis {untilStr}
                                                                            </div>
                                                                        );
                                                                    }
                                                                    const ms = getInactiveMs(client);
                                                                    if (ms === null) {
                                                                        return (
                                                                            <div className="flex items-center gap-1.5 mt-1.5 text-[9px] text-pe-muted font-bold">
                                                                                <span className="inline-block w-2 h-2 rounded-full bg-pe-muted shrink-0" />
                                                                                Noch keine Aktivität
                                                                            </div>
                                                                        );
                                                                    }
                                                                    const MS_ONLINE = 20 * 60 * 1000;
                                                                    const HOUR = 60 * 60 * 1000;
                                                                    const DAY = 24 * HOUR;
                                                                    if (ms < MS_ONLINE) {
                                                                        return (
                                                                            <div className="flex items-center gap-1.5 mt-1.5 text-[9px] text-emerald-400 font-bold">
                                                                                <span className="inline-block w-2 h-2 rounded-full bg-emerald-400 animate-pulse shrink-0" />
                                                                                Gerade online
                                                                            </div>
                                                                        );
                                                                    }
                                                                    let label: string;
                                                                    if (ms < HOUR) {
                                                                        label = `Vor ${Math.floor(ms / 60000)} Min aktiv`;
                                                                    } else if (ms < DAY) {
                                                                        label = `Vor ${Math.floor(ms / HOUR)} Std aktiv`;
                                                                    } else {
                                                                        label = `Vor ${Math.floor(ms / DAY)} Tagen aktiv`;
                                                                    }
                                                                    const isAcute = ms >= 90 * DAY;
                                                                    const isWarn = ms >= 60 * DAY;
                                                                    const colorClass = isAcute ? 'text-red-700 dark:text-red-400' : isWarn ? 'text-amber-800 dark:text-amber-400' : 'text-pe-muted';
                                                                    const dotColor = isAcute ? 'bg-red-400' : isWarn ? 'bg-amber-400' : 'bg-pe-muted';
                                                                    return (
                                                                        <div className={`flex items-center gap-1.5 mt-1.5 text-[9px] font-bold ${colorClass}`}>
                                                                            <span className={`inline-block w-2 h-2 rounded-full ${dotColor} shrink-0`} />
                                                                            <ClockIcon size={10} /> {label}
                                                                            {isAcute && (
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        const until = new Date(Date.now() + 28 * 24 * 60 * 60 * 1000);
                                                                                        updateDoc(doc(db, 'kalkulation_mandanten', client.id), { snoozedUntil: until.toISOString() })
                                                                                            .catch(() => alert('Snooze fehlgeschlagen.'));
                                                                                    }}
                                                                                    className="ml-1 px-1.5 py-0.5 rounded bg-pe-card border border-pe-border-strong text-[8px] font-black uppercase tracking-widest text-pe-muted hover:text-pe-text hover:border-blue-500/50 transition-colors"
                                                                                    title="4 Wochen pausieren — verschwindet aus dem 90-Tage-Radar"
                                                                                >
                                                                                    4W Pause
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                })()}
                                                            </td>
                                                            <td className="p-4">
                                                                <div className="flex flex-wrap items-center gap-2 mb-1">
                                                                    {displayKundenNr.includes('wird gesetzt') ? (
                                                                        <div className="text-sm font-black tracking-widest text-pe-muted bg-pe-tile-header dark:bg-pe-card inline-block px-2 py-0.5 rounded border border-pe-border-strong whitespace-nowrap">
                                                                            {displayKundenNr}
                                                                        </div>
                                                                    ) : (
                                                                        <MandantNrPill
                                                                            displayNr={displayKundenNr}
                                                                            betreuer={coachObj?.name || client.betreuer || client.coachId}
                                                                            team={team}
                                                                            tarifLabel={tarifBadge?.label}
                                                                            tarifClassName={tarifBadge?.className}
                                                                            size="sm"
                                                                        />
                                                                    )}
                                                                    {kdPending && (
                                                                        <span className="bg-pe-hover text-pe-muted border border-pe-border-strong/40 px-1.5 py-0.5 rounded text-[8px] uppercase tracking-widest font-black shrink-0">
                                                                            Mandant-Nummer nach Freigabe
                                                                        </span>
                                                                    )}
                                                                    {isBillingPaused && (
                                                                        <span className="bg-amber-100 text-amber-950 border border-amber-500 dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/30 px-1.5 py-0.5 rounded text-[8px] uppercase tracking-widest font-black shrink-0">
                                                                            Abrechnung pausiert
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <div className="text-[9px] text-pe-muted font-mono mb-2" title="Firebase Datenbank ID">
                                                                    DB-ID: {client.id}
                                                                </div>
                                                                <span className={`inline-flex items-center px-2 py-1 rounded text-[10px] font-bold uppercase tracking-widest ${isMyClient ? 'bg-[#E6C299]/20 text-pe-accent-text border border-[#E6C299]/30' : 'bg-pe-tile-header dark:bg-pe-card text-pe-muted dark:text-pe-muted border border-pe-border-strong dark:border-pe-border-strong'}`}>
                                                                    Coach: {coachDisplayName}
                                                                </span>
                                                            </td>
                                                            <td className="p-4">
                                                                <div className="flex flex-col gap-1.5 items-start">
                                                                    {client.latestStatus === 'datenerfassung' && (
                                                                        <span className="bg-purple-100 text-purple-900 border border-purple-400 dark:bg-purple-500/10 dark:text-purple-400 dark:border-purple-500/20 px-2 py-1 rounded text-[9px] font-bold uppercase tracking-widest flex items-center gap-1 w-max"><ClockIcon size={12} /> Noch in Erfassung</span>
                                                                    )}
                                                                    {client.latestStatus === 'aktiv' && (
                                                                        <span className="bg-emerald-100 text-emerald-900 border border-emerald-400 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20 px-2 py-1 rounded text-[9px] font-bold uppercase tracking-widest flex items-center gap-1 w-max"><CheckCircleIcon size={12} /> Aktiv</span>
                                                                    )}
                                                                    {(client.latestStatus === 'angefragt' || (client.latestStatus !== 'datenerfassung' && client.latestStatus !== 'aktiv')) && (
                                                                        <span className="bg-amber-100 text-amber-950 border border-amber-500 dark:bg-yellow-500/10 dark:text-yellow-500 dark:border-yellow-500/20 px-2 py-1 rounded text-[9px] font-bold uppercase tracking-widest flex items-center gap-1 w-max"><AlertTriangleIcon size={12} /> Übertragen · Prüfung</span>
                                                                    )}
                                                                    
                                                                    <span className="bg-pe-tile-header dark:bg-pe-card text-pe-text border border-pe-border-strong dark:border-pe-border-strong px-2 py-0.5 rounded text-sm font-black tracking-widest flex items-center gap-1.5 w-max">
                                                                        <CalendarIcon size={12} className="text-pe-text shrink-0" /> {laufzeitText}
                                                                    </span>

                                                                    {client.isProBono ? (
                                                                        <span className="bg-pink-100 text-pink-900 border border-pink-400 dark:bg-pink-500/10 dark:text-pink-400 dark:border-pink-500/20 px-2 py-1 rounded text-[9px] font-bold uppercase tracking-widest flex items-center gap-1 w-max">
                                                                            <HeartIcon size={10} /> Pro Bono {client.proBonoUntil ? `(bis ${client.proBonoUntil})` : ''}
                                                                        </span>
                                                                    ) : (
                                                                        (() => {
                                                                            const netto = Number(client.monthlyRevenue) || 0;
                                                                            const mwst = getCountryMwstSatz(settings as any);
                                                                            const brutto = netToGross(netto, mwst);
                                                                            return (
                                                                                <div className="flex flex-col gap-0.5 items-start">
                                                                                    <span className="bg-pe-card text-pe-muted border border-pe-border-strong px-2 py-1 rounded text-[9px] font-bold uppercase tracking-widest w-max">
                                                                                        Netto: {formatEuroDe(netto)} €
                                                                                        {client.hasFixedFee ? ' (Fix)' : ''}
                                                                                    </span>
                                                                                    <span className="bg-[#E6C299]/15 text-pe-accent-text border border-[#E6C299]/35 px-2 py-1 rounded text-[9px] font-black uppercase tracking-widest w-max">
                                                                                        Brutto: {formatEuroDe(brutto)} € · {mwst} % MwSt
                                                                                    </span>
                                                                                </div>
                                                                            );
                                                                        })()
                                                                    )}

                                                                    {client.stripeDisplay && !client.isProBono && (
                                                                        <span
                                                                            className={`px-2 py-1 rounded text-[9px] font-bold uppercase tracking-widest border flex items-center gap-1 w-max ${client.stripeColor}`}
                                                                            title={
                                                                                client.stripeCritical
                                                                                    ? STRIPE_PAYMENT_LOCK_ARMED
                                                                                        ? 'Zahlungsstop — Zugang gesperrt'
                                                                                        : 'Zahlungsstop erkannt — Sperre noch nicht scharfgeschaltet'
                                                                                    : client.stripeDisplay
                                                                            }
                                                                        >
                                                                            💳 {client.stripeDisplay}
                                                                            {client.stripeCritical && !STRIPE_PAYMENT_LOCK_ARMED && (
                                                                                <span className="text-[8px] opacity-70 normal-case tracking-normal">(Sperre aus)</span>
                                                                            )}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td className="p-4 text-right">
                                                                <div className="inline-flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() =>
                                                                            setTermineClient({
                                                                                id: client.id,
                                                                                name: client.fullName || client.firmenname || 'Mandant',
                                                                                coachId: coachObj?.id || currentUserProfile?.id,
                                                                                coachName: coachObj?.name || currentUserProfile?.name,
                                                                                coachAvatarUrl:
                                                                                    (coachObj as any)?.imageUrl ||
                                                                                    (currentUserProfile as any)?.imageUrl,
                                                                            })
                                                                        }
                                                                        className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded text-xs font-black uppercase transition-colors shadow-lg bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/25"
                                                                        title="1:1 Coaching-Termine verwalten"
                                                                    >
                                                                        <CalendarIcon size={16} /> Termine
                                                                    </button>
                                                                    <button 
                                                                        onClick={() => {
                                                                            if (client.isArchived) {
                                                                                localStorage.setItem('CURRENT_YEAR_STATUS', 'abgeschlossen');
                                                                            } else {
                                                                                localStorage.setItem('CURRENT_YEAR_STATUS', client.latestStatus);
                                                                            }
                                                                            startImpersonation(client.id, client.fullName);
                                                                        }} 
                                                                        className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded text-xs font-black uppercase transition-colors shadow-lg ${isMyClient ? 'bg-[#E6C299] text-black hover:bg-white' : 'bg-pe-card text-pe-muted border border-pe-border-strong hover:bg-pe-hover'}`}
                                                                    >
                                                                        <ExternalLinkIcon size={16} /> Einloggen
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )
                                                })}
                                                {displayCrmClients.length === 0 && (
                                                    <tr><td colSpan={4} className="text-center py-8 text-pe-muted">Keine passenden Akten gefunden.</td></tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* TAB: PEPulse */}
                    {activeTab === 'pepulse' && canAccessPepulse && (
                        <div className="animate-fade-in">
                            <MobileTrackerView
                                isAdmin={isAdminUser}
                                payoutBaseByEmail={Object.fromEntries(
                                    (activeTeam || [])
                                        .filter((m: any) => m && m.email)
                                        .map((m: any) => {
                                            const d = getCoachFinancials(m);
                                            return [
                                                String(m.email).trim().toLowerCase(),
                                                Number(d.pepulseMalusBase || 0),
                                            ];
                                        })
                                )}
                            />
                        </div>
                    )}

                    {/* TAB 2: FINANZEN (AUSGELAGERT) */}
                    {activeTab === 'finanzen' && (
                        <FinanzTresor 
                            isGlobalView={isGlobalView}
                            isAdminUser={isAdminUser}
                            adminFilter={adminFilter}
                            setAdminFilter={(val: string) => { setAdminFilter(val); setIsInvoiceExpanded(false); }}
                            coachDropdownTeam={coachDropdownTeam}
                            coachClientCountMap={coachClientCountMap}
                            targetCoachName={targetCoachName}
                            currentUserEmail={currentUserEmail}
                            previewCoachProfile={previewCoachProfile}
                            realClients={realClients}
                            activeTeam={activeTeam}
                            TEAM_SPLIT={TEAM_SPLIT}
                            clearingItems={clearingItems}
                            historyItems={historyItems}
                            archiveItems={archiveItems} // 💉 CHIRURGIE-FIX: Prop-Bridge etabliert!
                            isClearingLoading={isClearingLoading}
                            isArchiveLoading={isArchiveLoading}
                            isDirty={isDirty}
                            invoiceSuccessMsg={invoiceSuccessMsg}
                            isGeneratingInvoices={isGeneratingInvoices}
                            sections={sections}
                            expandedArchiveMonths={expandedArchiveMonths}
                            toggleSection={toggleSection}
                            toggleArchiveMonth={toggleArchiveMonth}
                            handleAddClearingItem={handleAddClearingItem}
                            handleUpdateClearingItem={handleUpdateClearingItem}
                            handleFocus={handleFocus}
                            archiveItem={archiveItem}
                            setDeleteModalOpen={setDeleteModalOpen}
                            deleteArchiveInvoice={deleteArchiveInvoice}
                            handleGenerateInvoices={handleGenerateInvoices}
                            openLivePrognosisFor={openLivePrognosisFor}
                            openArchivedInvoice={openArchivedInvoice}
                            sendMonthMailsToAll={handleSendMonthMailsToAll}
                            bulkMailStatus={bulkMailStatus}
                            showEInvoiceSettings={canManageEInvoice}
                            eInvoiceReady={eInvoiceStatus.eInvoiceReady}
                            onOpenEInvoiceSettings={() => setEInvoiceSettingsOpen(true)}
                            getRevenueSplit={getRevenueSplit}
                            getAmount={getAmount}
                            getCoachFinancials={getCoachFinancials}
                            calculateClearingEffect={calculateClearingEffect}
                        />
                    )}

                    {/* TAB 3: RECHNUNGEN (AUSGELAGERT) */}
                    {activeTab === 'rechnungen' && (
                        <div className="animate-fade-in w-full flex flex-col relative z-0">
                            {canManageEInvoice ? (
                                <div
                                    className={`mb-4 flex flex-col gap-3 rounded-xl border px-4 py-3 sm:flex-row sm:items-center sm:justify-between ${
                                        eInvoiceStatus.eInvoiceReady
                                            ? 'border-emerald-800/50 bg-emerald-950/20'
                                            : 'border-amber-800/50 bg-amber-950/20'
                                    }`}
                                >
                                    <div className="min-w-0">
                                        <p
                                            className={`text-xs font-black uppercase tracking-widest ${
                                                eInvoiceStatus.eInvoiceReady ? 'text-emerald-300' : 'text-amber-200'
                                            }`}
                                        >
                                            {eInvoiceStatus.eInvoiceReady
                                                ? 'E-Rechnung bereit'
                                                : 'E-Rechnungsdaten unvollständig'}
                                        </p>
                                        <p className="mt-1 text-xs text-pe-muted">
                                            {isCurrentUserOwner
                                                ? 'Auch als Owner: für deine Mandanten-Rechnungen (Coach-Invoice) IBAN, Steuer und Adresse hier hinterlegen — sonst keine E-Rechnung.'
                                                : 'Für Mandanten- und Honorarrechnungen: IBAN, Steuer und Adresse — nur deine Daten.'}
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setEInvoiceSettingsOpen(true)}
                                        className={`shrink-0 rounded-lg px-4 py-2.5 text-[10px] font-black uppercase tracking-widest transition-colors ${
                                            eInvoiceStatus.eInvoiceReady
                                                ? 'border border-emerald-700/60 bg-emerald-900/40 text-emerald-200 hover:bg-emerald-900/60'
                                                : 'bg-[#E6C299] text-black hover:bg-[#d4b086]'
                                        }`}
                                    >
                                        {eInvoiceStatus.eInvoiceReady
                                            ? 'Meine Daten bearbeiten'
                                            : 'Meine Rechnungsdaten eintragen'}
                                    </button>
                                </div>
                            ) : null}
                            <div className="mb-6 flex justify-between items-center shrink-0 w-full">
                                <div>
                                    <h1 className="text-3xl font-bold text-pe-text tracking-tight mb-2 flex items-center gap-4">Rechnungs-Center</h1>
                                    <p className="text-pe-muted">Verwalte und erstelle Consulting-Rechnungen für deine Mandanten.</p>
                                </div>
                                <button 
                                    onClick={() => {
                                        setEditingClientInvoice(null);
                                        setInvoiceView('create');
                                    }} 
                                    className="bg-[#E6C299] hover:bg-white text-black font-black uppercase tracking-widest px-5 py-3 rounded-lg shadow-lg transition-colors flex items-center gap-2"
                                >
                                    <PlusIcon size={18} /> Neue Rechnung
                                </button>
                            </div>
                            
                            <RechnungsCenter 
                                isClientInvoicesLoading={isClientInvoicesLoading}
                                clientInvoices={clientInvoices}
                                selectedHistoryInvoice={selectedHistoryInvoice}
                                isCreatingClientInvoice={isCreatingClientInvoice}
                                activeClientInvoicePreviewData={activeClientInvoicePreviewData}
                                formatCurrency={formatCurrency}
                                setSelectedHistoryInvoice={setSelectedHistoryInvoice}
                                handleEditInvoice={handleEditInvoice}
                                sendClientManualInvoiceMail={sendClientManualInvoiceMail}
                                deleteClientInvoice={deleteClientInvoice}
                                downloadClientManualInvoice={downloadClientManualInvoice}
                                ClientManualInvoicePrintTemplate={ClientManualInvoicePrintTemplate}
                            />
                        </div>
                    )}
                    </>
                    )}
                </main>
            </div>
            
            {/* OFFSCREEN PRINT FACTORY FÜR COACH HONORARE */}
            {isInvoiceExpanded && previewInvoiceData && (
                <div className="fixed inset-0 z-[9999] bg-stone-900/50 dark:bg-black/90 backdrop-blur-md overflow-y-auto print:absolute print:inset-0 print:bg-white print:p-0 print:overflow-visible print:block">
                    <style type="text/css">
                        {`
                        @page {
                            size: A4 portrait !important;
                            margin: 0 !important;
                        }
                        @media print {
                            html, body {
                                width: 210mm !important;
                                height: 297mm !important;
                                margin: 0 !important;
                                padding: 0 !important;
                                background: #ffffff !important;
                                -webkit-print-color-adjust: exact !important;
                                print-color-adjust: exact !important;
                            }
                            body * { visibility: hidden !important; }
                            #coach-invoice-print-area,
                            #coach-invoice-print-area * {
                                visibility: visible !important;
                            }
                            #coach-invoice-print-area {
                                position: absolute !important;
                                left: 0 !important;
                                top: 0 !important;
                                width: 210mm !important;
                                min-height: 297mm !important;
                                margin: 0 !important;
                                padding: 0 !important;
                                box-shadow: none !important;
                                background: #ffffff !important;
                            }
                            .invoice-print-container {
                                width: 210mm !important;
                                min-height: 297mm !important;
                                padding: 15mm 20mm !important;
                                box-sizing: border-box !important;
                                margin: 0 auto !important;
                            }
                            .no-print { display: none !important; }
                        }
                        `}
                    </style>

                    <div className="fixed top-4 right-4 flex gap-3 z-[10000] no-print print:hidden">
                        {previewInvoiceData.issueDate !== 'LIVE-KALKULATION' && (
                            <button
                                onClick={handleSendInvoiceMail}
                                disabled={isSendingInvoiceMail || isExportingPdf}
                                className="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 disabled:cursor-wait text-black font-black text-xs uppercase tracking-widest py-3 px-6 rounded-full shadow-[0_0_30px_rgba(16,185,129,0.4)] flex items-center gap-2 transition-transform hover:scale-105"
                            >
                                <MailIcon size={16} /> {isSendingInvoiceMail ? 'Wird gesendet…' : 'Per E-Mail senden'}
                            </button>
                        )}
                        <button 
                            onClick={handlePdfDownload}
                            disabled={isExportingPdf || isSendingInvoiceMail}
                            className="bg-[#E6C299] hover:bg-[#d4b08c] disabled:opacity-60 disabled:cursor-wait text-black font-black text-xs uppercase tracking-widest py-3 px-6 rounded-full shadow-[0_0_30px_rgba(230,194,153,0.4)] flex items-center gap-2 transition-transform hover:scale-105"
                        >
                            <DownloadIcon size={16} /> {isExportingPdf ? 'PDF wird erstellt…' : 'Als PDF Speichern'}
                        </button>
                        <button 
                            onClick={() => { setIsInvoiceExpanded(false); setPreviewInvoiceData(null); }} 
                            className="bg-pe-card hover:bg-pe-hover text-pe-text p-3 rounded-full shadow-2xl border border-pe-border-strong flex items-center justify-center transition-transform hover:scale-105"
                        >
                            <XIcon size={24} />
                        </button>
                    </div>

                    <div className="min-h-screen py-10 px-4 flex justify-center items-start print:block print:min-h-0 print:py-0 print:px-0 print:m-0">
                        <div id="coach-invoice-print-area" className="relative w-[210mm] min-h-[297mm] shadow-[0_0_50px_rgba(0,0,0,0.5)] bg-white print:w-[210mm] print:max-w-none print:h-auto print:min-h-[297mm] print:m-0 print:shadow-none print:border-none overflow-hidden">
                            {previewInvoiceData.issueDate === 'LIVE-KALKULATION' && (
                                <div className="absolute inset-0 pointer-events-none flex items-center justify-center opacity-10 z-0 overflow-hidden no-print print:hidden">
                                    <div className="transform -rotate-45 text-[150px] font-black text-black leading-none whitespace-nowrap">
                                        PROGNOSE
                                    </div>
                                </div>
                            )}
                            <div className="relative z-10">
                                <CoachInvoice
                                    coach={previewInvoiceData.coach}
                                    invoiceNumber={previewInvoiceData.invoiceNumber}
                                    issueDate={previewInvoiceData.issueDate}
                                    targetMonthText={previewInvoiceData.targetMonthText}
                                    revenueNet={previewInvoiceData.revenueNet}
                                    managementBonus={previewInvoiceData.managementBonus}
                                    clearingTotal={previewInvoiceData.clearingTotal}
                                    supervisedClientCount={previewInvoiceData.supervisedClientCount}
                                    pepulseMalus={previewInvoiceData.pepulseMalus}
                                    pepulseMalusRate={previewInvoiceData.pepulseMalusRate}
                                    pepulsePoints={previewInvoiceData.pepulsePoints}
                                    pepulseMalusBase={previewInvoiceData.pepulseMalusBase}
                                    pepulseTargetPoints={previewInvoiceData.pepulseTargetPoints}
                                    pepulseVacationDays={previewInvoiceData.pepulseVacationDays}
                                    pepulsePrevMonth={previewInvoiceData.pepulsePrevMonth}
                                    pepulseRedistributionBonus={previewInvoiceData.pepulseRedistributionBonus}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* OFFSCREEN PRINT FACTORY FÜR MANDANTEN RECHNUNGEN */}
            <div id="client-manual-print-factory" style={{ position: 'absolute', top: '-50000px', left: '-50000px' }}>
                <div style={{ width: '794px' }}>
                    <ClientManualInvoicePrintTemplate data={activeClientInvoicePreviewData} />
                </div>
            </div>

            {canManageEInvoice ? (
                <CoachEInvoiceSettingsModal
                    isOpen={eInvoiceSettingsOpen}
                    onClose={() => setEInvoiceSettingsOpen(false)}
                    coachId={String(currentUserId || '')}
                    profile={coachEInvoiceProfile}
                    suggestedDefaults={
                        isCurrentUserOwner ? PREISEXPERT_OWNER_EINVOICE_DEFAULTS : null
                    }
                    onSaved={setCoachEInvoiceProfile}
                />
            ) : null}

            {showIntakeHub && (
                <IntakeHubModal onClose={() => setShowIntakeHub(false)} />
            )}

            {termineClient && (
                <>
                    <div
                        className="fixed inset-0 z-[90000] bg-black/70 backdrop-blur-sm"
                        onClick={() => setTermineClient(null)}
                    />
                    <div className="fixed inset-y-0 right-0 z-[90001] w-full max-w-xl bg-pe-bg border-l border-pe-border shadow-2xl flex flex-col animate-fade-in">
                        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-pe-border bg-pe-card">
                            <div className="min-w-0">
                                <p className="text-[10px] font-black uppercase tracking-widest text-emerald-400">
                                    1:1 Coaching-Termine
                                </p>
                                <h3 className="text-lg font-black text-pe-text dark:text-white truncate">
                                    {termineClient.name}
                                </h3>
                            </div>
                            <button
                                type="button"
                                onClick={() => setTermineClient(null)}
                                className="p-2 rounded-full bg-pe-card hover:bg-pe-hover text-pe-muted border border-pe-border"
                            >
                                <XIcon size={20} />
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-5">
                            <CoachingTerminePanel
                                clientId={termineClient.id}
                                clientLabel={termineClient.name}
                                defaultCoach={
                                    (termineClient.coachId
                                        ? {
                                              id: termineClient.coachId,
                                              name: termineClient.coachName || 'Coach',
                                              imageUrl: termineClient.coachAvatarUrl,
                                          }
                                        : null) ||
                                    currentUserProfile ||
                                    null
                                }
                                coachOptions={getAssignableTeam(fullTeam)}
                                showNotification={(msg, type) => {
                                    if (type === 'error') window.alert(msg);
                                }}
                            />
                        </div>
                    </div>
                </>
            )}

        </div>
    );
};

export default CoachDashboard;